import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { adminStatsAPI, adminTeamsAPI, adminSeasonsAPI, adminLeagueManagementAPI } from '../services/adminApi';
import { divisionsAPI } from '../services/divisionsApi';
import AdminDivisionsSection from '../components/AdminDivisionsSection';
import AdminLeagueManagementV2 from '../components/AdminLeagueManagementV2';
import AdminSeasonsManagement from '../components/AdminSeasonsManagement';
import AdminMatchesNew from '../components/AdminMatchesNew';
import AdminMatchesByDivision from '../components/AdminMatchesByDivision';
import AdminScheduledMatches from '../components/AdminScheduledMatches';
import AdminTeammateFinderSection from '../components/AdminTeammateFinderSection';
import AdminSettingsGeneral from '../components/AdminSettingsGeneral';
import AdminSettingsClubs from '../components/AdminSettingsClubs';
import AdminOrganisedEvents from '../components/AdminOrganisedEvents';
import AdminSettingsPromoCreate from '../components/AdminSettingsPromoCreate';
import AdminSettingsRegistrationProducts from '../components/AdminSettingsRegistrationProducts';
import AdminRegistrationOrders from '../components/AdminRegistrationOrders';
import AdminShop from '../components/AdminShop';
import AdminAnalyticsHub, {
  ANALYTICS_HUB_SECTION_TO_TAB,
  canAccessAnalyticsHub,
} from '../components/AdminAnalyticsHub';
import AdminPromotionSystemHub, {
  PROMOTION_HUB_SECTION_TO_TAB,
} from '../components/AdminPromotionSystemHub';
import AdminPlayersSection from '../components/AdminPlayersSection';
import { setAdminNavHandoff, consumeAdminNavHandoff } from '../utils/adminNavFilters';
import AdminSettingsTournaments from '../components/AdminSettingsTournaments';
import AdminSettingsEmail from '../components/AdminSettingsEmail';
import AdminUsersAndRolesSection from '../components/AdminUsersAndRolesSection';
import AdminAuditLogPanel from '../components/AdminAuditLogPanel';
import AdminTeamProvisioningPanel from '../components/AdminTeamProvisioningPanel';
import AdminTeamMemberProfileCard, { AdminTeamMembersGrid } from '../components/AdminTeamMemberProfileCard';
import { useDivisionLockStatus, DivisionLockWarning } from '../hooks/useDivisionLock';
import { useAdminSeasonPhaseFilter } from '../hooks/useAdminSeasonPhaseFilter';
import AdminSeasonPhaseFilters from '../components/AdminSeasonPhaseFilters';
import { trackEvent } from '../utils/facebookPixel';
import { clearAdminTenantInfo, setAdminTenantInfo, getAdminTenantInfo } from '../utils/tenantHelper';
import { adminTenantBySlugAPI } from '../services/adminApi';
import { mapToNewCanton } from '../utils/cantonMapping';
import { getSkillRangeFromDivisionName } from '../utils/divisionUtils';

function isAdminTeamSeasonForfeit(team) {
  return String(team?.season_exit_status || '').toLowerCase() === 'forfeit';
}

function adminTeamStatusDisplay(team) {
  const active = team.season_is_active !== undefined ? team.season_is_active : team.is_active;
  if (active) return { label: '✅ Active', color: '#059669' };
  if (isAdminTeamSeasonForfeit(team)) return { label: '🏳️ Forfeit', color: '#9f1239' };
  return { label: '❌ Disabled', color: '#dc2626' };
}

const EDIT_CANTON_ALIASES = {
  BS: 'Basel',
  BL: 'Basel',
  'BASEL-STADT': 'Basel',
  'BASEL-LANDSCHAFT': 'Basel',
  BASEL: 'Basel',
  AR: 'Appenzell',
  AI: 'Appenzell',
  NW: 'Nidwalden',
  OW: 'Nidwalden',
  UR: 'Nidwalden',
  GR: 'Grisons',
  GRAUBUNDEN: 'Grisons',
  GRAUBÜNDEN: 'Grisons',
  GENEVA: 'GE',
  GENEVE: 'GE',
  GENÈVE: 'GE',
  BERN: 'BE',
  BERNE: 'BE',
  FRIBOURG: 'FR',
  LUCERNE: 'LU',
  GLARUS: 'GL',
  JURA: 'JU',
  NEUCHATEL: 'NE',
  NEUCHÂTEL: 'NE',
};

function normalizeEditCanton(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const mapped = mapToNewCanton(raw) || mapToNewCanton(raw.toUpperCase());
  if (mapped && mapped !== raw) return mapped;
  const alias = EDIT_CANTON_ALIASES[raw] || EDIT_CANTON_ALIASES[raw.toUpperCase()];
  return alias || mapped || raw;
}

function sameAdminCanton(a, b) {
  return normalizeEditCanton(a) === normalizeEditCanton(b);
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value != null && String(value).trim() !== '') return value;
  }
  return '';
}

/** Playing canton for Edit team: assigned division this phase, then list/TSR, not home teams.canton. */
function resolveEditPlayingCanton(team, details, divisions) {
  const divisionId = firstNonEmpty(details?.division_id, team?.division_id);
  const division = (divisions || []).find((d) => String(d.id) === String(divisionId));
  return normalizeEditCanton(
    firstNonEmpty(
      division?.canton,
      division?.original_canton,
      team?.canton,
      details?.canton
    )
  );
}

function divisionsForEditCanton(divisions, canton) {
  const mapped = normalizeEditCanton(canton);
  if (!mapped) return [];
  return (divisions || []).filter((division) =>
    sameAdminCanton(division.canton || division.original_canton, mapped)
  );
}

function withCurrentDivision(options, team) {
  const list = Array.isArray(options) ? [...options] : [];
  const currentId = firstNonEmpty(team?.division_id);
  if (!currentId || list.some((d) => String(d.id) === String(currentId))) return list;
  list.unshift({
    id: currentId,
    name: team.division_name || 'Current division',
    canton: normalizeEditCanton(team.canton) || team.canton || '',
    skill_level_min: team.skill_level_min,
    skill_level_max: team.skill_level_max,
  });
  return list;
}

function formatTeamEditDivisionLabel(division) {
  const min = division.skill_level_min ?? division.skillLevelMin;
  const max = division.skill_level_max ?? division.skillLevelMax;
  let range = '';
  if (min != null && min !== '' && max != null && max !== '') {
    range = ` (${min}-${max})`;
  } else {
    const bareName = String(division.name || '')
      .replace(/^(BS|BL|Basel|AR|AI|Appenzell|NW|OW|UR|Nidwalden|GR|Grisons)\s+/i, '')
      .trim();
    const fromName = getSkillRangeFromDivisionName(bareName);
    if (fromName?.range) range = ` (${fromName.range})`;
  }
  const name = String(division.name || '').trim();
  const canton = division.canton || '';
  if (canton && name && name.includes(canton)) return `${name}${range}`;
  if (canton && name) return `${canton} ${name}${range}`;
  return `${name || 'Division'}${range}`;
}

/** Map dashboard section id to permission section key (for role-based menu filtering) */
const SECTION_TO_PERMISSION = {
  overview: 'stats',
  'analytics-hub': 'stats', // hub may also show via child perms — see filteredMenuItems
  analytics: 'stats',
  'league-statistics': 'stats',
  attention: 'stats',
  teams: 'teams',
  'team-provisioning': 'teams',
  'registration-analytics': 'registration_analytics',
  divisions: 'divisions',
  // Nested under Divisions hub; open with divisions read; lock tab gated separately
  'division-lock': 'divisions',
  'league-management': 'league_management',
  'promotion-system': 'promotion_relegation',
  'promotion-relegation': 'promotion_relegation',
  'campaign-carry-promotion': 'promotion_relegation',
  'promo-codes': 'promo_codes',
  'partner-communications': 'partner_communications',
  'teammate-finder': 'teammate_finder',
  players: 'teams', // same as teams: visible when on country tenant
  'scheduled-matches': 'scheduled_matches',
  matches: 'matches',
  'matches-by-division': 'matches',
  'settings-general': 'settings_general',
  'settings-clubs': 'settings_general',
  'settings-seasons': 'settings_seasons',
  'organised-events': 'organised_events',
  'settings-promo-codes': 'settings_promo_codes',
  'settings-registration-products': 'settings_registration_products',
  'registration-orders': 'settings_registration_products',
  shop: 'shop',
  'settings-tournaments': 'settings_tournaments',
  'settings-analytics': 'settings_general',
  'settings-email': 'settings_general',
  'admin-users-and-roles': 'admin_users_and_roles',
  'audit-log': 'audit_log',
};

/** Legacy sidebar sections remapped into Analytics / Promotion hubs */
const LEGACY_SECTION_REDIRECT = {
  ...Object.fromEntries(
    Object.keys(ANALYTICS_HUB_SECTION_TO_TAB).map((id) => [
      id,
      { section: 'analytics-hub', analyticsHubTab: ANALYTICS_HUB_SECTION_TO_TAB[id] },
    ])
  ),
  ...Object.fromEntries(
    Object.keys(PROMOTION_HUB_SECTION_TO_TAB).map((id) => [
      id,
      { section: 'promotion-system', promotionHubTab: PROMOTION_HUB_SECTION_TO_TAB[id] },
    ])
  ),
};

/** Normalize division id for comparisons (treat null, "", and whitespace as empty). */
function normAdminDivisionId(v) {
  return v == null || v === '' ? null : String(v).trim();
}

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  display: flex;
  position: relative;
  overflow-x: visible;
  
  @media (max-width: 768px) {
    flex-direction: column;
    overflow-x: hidden;
  }
`;

const Sidebar = styled.aside`
  width: 250px;
  background: #1f2937;
  color: white;
  padding: 0;
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  z-index: 1000;
  transition: transform 0.3s ease;
  
  @media (max-width: 768px) {
    transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    width: 280px;
    box-shadow: ${props => props.$isOpen ? '2px 0 10px rgba(0, 0, 0, 0.1)' : 'none'};
  }
`;

const SidebarOverlay = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 1001;
  background: #1f2937;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem;
  cursor: pointer;
  font-size: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
  
  &:hover {
    background: #374151;
  }
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const SidebarHeader = styled.div`
  padding: 2rem 1.5rem;
  border-bottom: 1px solid #374151;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
  
  .logo-section {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 1rem;
    
    img {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      
      @media (max-width: 768px) {
        width: 36px;
        height: 36px;
      }
    }
    
    h1 {
      font-size: 1.2rem;
      font-weight: 700;
      margin: 0;
      
      @media (max-width: 768px) {
        font-size: 1.1rem;
      }
    }
  }
  
  .admin-info {
    p {
      margin: 0;
      font-size: 0.875rem;
      color: #d1d5db;
      
      @media (max-width: 768px) {
        font-size: 0.8rem;
      }
    }
  }
`;

const Navigation = styled.nav`
  padding: 1rem 0;
  
  @media (max-width: 768px) {
    padding: 0.5rem 0;
  }
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
  border-left: 3px solid transparent;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  &.active {
    background: rgba(30, 64, 175, 0.2);
    border-left-color: #043663;
  }
  
  .nav-icon {
    font-size: 1.25rem;
    width: 20px;
    text-align: center;
    flex-shrink: 0;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
  
  .nav-text {
    font-weight: 500;
    flex: 1;
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  }
  
  .nav-arrow {
    font-size: 0.875rem;
    transition: transform 0.3s ease;
    transform: ${props => props.$isOpen ? 'rotate(90deg)' : 'rotate(0deg)'};
    
    @media (max-width: 768px) {
      font-size: 0.8rem;
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;

const AccordionSubmenu = styled.div`
  /* Keep accordion animation but avoid clipping settings entries on smaller viewports/zoom */
  max-height: ${props => props.$isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  background: rgba(0, 0, 0, 0.2);
`;

const AccordionSubItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.625rem 1.5rem 0.625rem 3.5rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
  border-left: 3px solid transparent;
  font-size: 0.9rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
  
  &.active {
    background: rgba(30, 64, 175, 0.3);
    border-left-color: #043663;
    font-weight: 600;
  }
  
  .nav-icon {
    font-size: 1.1rem;
    width: 18px;
    text-align: center;
    flex-shrink: 0;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem 1rem 0.625rem 2.5rem;
    font-size: 0.85rem;
  }
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 250px;
  padding: 2rem;
  overflow-x: visible;
  min-width: 0;
  
  @media (max-width: 768px) {
    margin-left: 0;
    padding: 1rem;
    padding-top: 4rem; /* Space for hamburger button */
    overflow-x: hidden;
  }
`;

const TopBar = styled.div`
  background: white;
  padding: 1rem 2rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`;

const PageTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const LogoutButton = styled.button`
  background: #d52b1e;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background: #b91c1c;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
  }
`;

const ContentArea = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  padding: 2rem;
  min-height: 600px;
  overflow-x: visible;
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 1440px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 1280px) {
    padding: 1.25rem;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 8px;
    min-height: auto;
    overflow-x: hidden;
  }
`;

const WelcomeSection = styled.div`
  text-align: center;
  padding: 3rem 0;
  
  @media (max-width: 768px) {
    padding: 2rem 0;
  }
  
  h3 {
    font-size: 1.5rem;
    color: #1f2937;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }
  
  p {
    color: #6b7280;
    font-size: 1.1rem;
    margin-bottom: 2rem;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, #043663 0%, #043663 100%);
  color: white;
  padding: 1.5rem;
  border-radius: 12px;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 8px;
  }
  
  .stat-number {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }
  
  .stat-label {
    font-size: 0.875rem;
    opacity: 0.9;
    
    @media (max-width: 768px) {
      font-size: 0.8rem;
    }
  }
`;

// Teams section styles
const TeamsContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const TeamsHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 100%;
  overflow-x: visible;
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 1440px) {
    padding: 1.25rem;
  }
  
  @media (max-width: 1280px) {
    padding: 1rem;
    gap: 0.75rem;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
    overflow-x: hidden;
  }
`;

const TeamsTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
`;

const SearchContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 1440px) {
    gap: 0.65rem;
  }
  
  @media (max-width: 1280px) {
    gap: 0.5rem;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    gap: 0.75rem;
  }
`;

const SearchInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  min-width: 180px;
  max-width: 250px;
  flex: 1 1 auto;
  
  &:focus {
    outline: none;
    border-color: #043663;
  }
  
  @media (max-width: 1440px) {
    min-width: 150px;
    max-width: 200px;
    font-size: 0.8rem;
    padding: 0.45rem 0.65rem;
  }
  
  @media (max-width: 1280px) {
    min-width: 140px;
    max-width: 180px;
    font-size: 0.75rem;
    padding: 0.4rem 0.6rem;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    font-size: 0.9rem;
    padding: 0.65rem 0.85rem;
  }
`;

const FilterSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  min-width: 140px;
  max-width: 200px;
  flex: 0 1 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  &:focus {
    outline: none;
    border-color: #043663;
  }
  
  @media (max-width: 1440px) {
    min-width: 120px;
    max-width: 160px;
    font-size: 0.8rem;
    padding: 0.45rem 0.65rem;
  }
  
  @media (max-width: 1280px) {
    min-width: 110px;
    max-width: 140px;
    font-size: 0.75rem;
    padding: 0.4rem 0.6rem;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    font-size: 0.9rem;
    padding: 0.65rem 0.85rem;
  }
`;

const TeamsTableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  position: relative;
  
  @media (max-width: 1440px) {
    margin: 0 -0.5rem;
    padding: 0 0.5rem;
  }
  
  @media (max-width: 768px) {
    margin: 0 -1rem;
    padding: 0 1rem;
  }
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #043663;
    border-radius: 4px;
  }
  
  /* Hide columns on smaller screens */
  .hide-on-small {
    @media (max-width: 1280px) {
      display: none !important;
    }
  }
  
  .hide-on-medium {
    @media (max-width: 1440px) {
      display: none !important;
    }
  }
`;

// Global styles for hiding columns on smaller screens (currently unused)
// eslint-disable-next-line no-unused-vars
const GlobalStyles = styled.div`
  @media (max-width: 1280px) {
    .hide-on-small {
      display: none !important;
    }
  }
  
  @media (max-width: 1440px) {
    .hide-on-medium {
      display: none !important;
    }
  }
`;

const TeamsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 1000px;
  
  @media (max-width: 1440px) {
    min-width: 900px;
    font-size: 0.85rem;
  }
  
  @media (max-width: 1280px) {
    min-width: 800px;
    font-size: 0.8rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    min-width: 700px;
  }
`;

const TableHeader = styled.thead`
  background: #f9fafb;
`;

const TableHeaderCell = styled.th`
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
  
  @media (max-width: 1440px) {
    padding: 0.65rem 0.75rem;
    font-size: 0.7rem;
  }
  
  @media (max-width: 1280px) {
    padding: 0.6rem 0.6rem;
    font-size: 0.65rem;
    letter-spacing: 0.02em;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.5rem;
    font-size: 0.6rem;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #f3f4f6;
  
  &:hover {
    background: #f9fafb;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 0.875rem;
  color: #1f2937;
  
  @media (max-width: 1440px) {
    padding: 0.75rem 0.75rem;
    font-size: 0.8rem;
  }
  
  @media (max-width: 1280px) {
    padding: 0.65rem 0.6rem;
    font-size: 0.75rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 0.5rem;
    font-size: 0.7rem;
  }
`;

const TeamName = styled.div`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
  transition: color 0.2s ease;
  
  &:hover {
    color: #043663;
  }
`;

const TeamDetails = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;

const DivisionBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: #dbeafe;
  color: #043663;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const SkillLevel = styled.span`
  font-weight: 600;
  color: #059669;
`;

const SourceBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => {
    switch(props.$source) {
      case 'meta': return '#e0e7ff';
      case 'google': return '#fef3c7';
      case 'direct': return '#f3f4f6';
      case 'referral': return '#fce7f3';
      case 'email': return '#dbeafe';
      case 'organic': return '#d1fae5';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch(props.$source) {
      case 'meta': return '#4338ca';
      case 'google': return '#92400e';
      case 'direct': return '#374151';
      case 'referral': return '#9f1239';
      case 'email': return '#1e40af';
      case 'organic': return '#065f46';
      default: return '#6b7280';
    }
  }};
`;

const ActionButton = styled.button`
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  color: #374151;
  font-size: 0.75rem;
  cursor: pointer;
  margin-right: 0.5rem;
  
  &:hover {
    background: #f9fafb;
  }
  
  &.primary {
    background: #043663;
    color: white;
    border-color: #043663;
    
    &:hover {
      background: #032a4a;
    }
  }
  
  &.danger {
    background: #d52b1e;
    color: white;
    border-color: #d52b1e;
    
    &:hover {
      background: #d52b1e;
    }
  }
`;

const LoadingContainer = styled.div`
  padding: 2rem;
  text-align: center;
  color: #6b7280;
`;

const ErrorContainer = styled.div`
  padding: 2rem;
  text-align: center;
  color: #d52b1e;
`;

// Team Details Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 2rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem 1rem;
  }
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0.5rem;
  border-radius: 8px;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const TeamLogoSection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const TeamLogoImg = styled.img`
  width: 96px;
  height: 96px;
  border-radius: 12px;
  object-fit: cover;
  border: 2px solid #e5e7eb;
`;

const TeamLogoPlaceholder = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 12px;
  background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TeamLogoActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const TeamInfoSection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
`;

const TeamInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const InfoItem = styled.div`
  .label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.25rem;
  }
  
  .value {
    font-size: 0.875rem;
    color: #1f2937;
    font-weight: 500;
  }
`;

const MembersSection = styled.div`
  margin-top: 2rem;
`;

const MembersTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
`;

const SkillLevelBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: ${props => {
    const level = parseFloat(props.$level);
    if (level >= 8) return '#dcfce7';
    if (level >= 6) return '#fef3c7';
    if (level >= 4) return '#fed7aa';
    return '#fee2e2';
  }};
  color: ${props => {
    const level = parseFloat(props.$level);
    if (level >= 8) return '#166534';
    if (level >= 6) return '#92400e';
    if (level >= 4) return '#9a3412';
    return '#991b1b';
  }};
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: #6b7280;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const PageButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  background: ${props => props.$active ? '#043663' : 'white'};
  color: ${props => props.$active ? 'white' : '#374151'};
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.$active ? '#043663' : '#f3f4f6'};
    border-color: #043663;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 0.25rem;
`;

// Meta Pixel Test Section Component (currently unused - comment out if not needed)
// eslint-disable-next-line no-unused-vars
const MetaPixelTestSection = () => {
  // eslint-disable-next-line no-unused-vars
  const _navigate = useNavigate();

  const testPaymentSuccess = () => {
    // Navigate to payment success page with test parameters
    const testUrl = `/payment-success?session_id=test_session_123&team_id=test_team_456&preview=true`;
    window.open(testUrl, '_blank');
  };

  const testPurchaseEvent = () => {
    console.log('🧪 Testing Purchase event directly...');
    trackEvent('Purchase', {
      value: 160,
      currency: 'CHF',
      content_name: 'Team Registration Fee (TEST)',
      content_category: 'Payment',
      content_ids: ['test_team_123'],
      num_items: 1
    });
    alert('Purchase event fired! Check console for debug info.');
  };

  const testLoginEvent = () => {
    console.log('🧪 Testing Login event directly...');
    trackEvent('Login', {
      content_name: 'Team Login (TEST)',
      content_category: 'Authentication'
    });
    alert('Login event fired! Check console for debug info.');
  };

  const testLeadEvent = () => {
    console.log('🧪 Testing Lead event directly...');
    trackEvent('Lead', {
      content_name: 'Team Registration (TEST)',
      content_category: 'Registration',
      value: 1,
      currency: 'CHF'
    });
    alert('Lead event fired! Check console for debug info.');
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h2 style={{ marginBottom: '1.5rem', color: '#1f2937' }}>🔍 Meta Pixel Test Center</h2>
      
      <div style={{ 
        background: '#f3f4f6', 
        padding: '1.5rem', 
        borderRadius: '8px', 
        marginBottom: '2rem' 
      }}>
        <h3 style={{ marginBottom: '1rem', color: '#374151' }}>Current Domain & Pixel Status</h3>
        <p><strong>Domain:</strong> {window.location.hostname}</p>
        <p><strong>Meta Pixel Loaded:</strong> {typeof window.fbq !== 'undefined' ? '✅ Yes' : '❌ No'}</p>
        <p><strong>Current URL:</strong> {window.location.href}</p>
      </div>

      <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        
        {/* Test Payment Success Page */}
        <div style={{ 
          background: 'white', 
          padding: '1.5rem', 
          borderRadius: '8px', 
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{ marginBottom: '1rem', color: '#1f2937' }}>🎯 Test Payment Success Page</h3>
          <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
            Opens the payment success page in a new tab with test parameters. This will trigger the actual Meta Pixel events.
          </p>
          <button 
            onClick={testPaymentSuccess}
            style={{
              background: '#10b981',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Open Payment Success Page
          </button>
        </div>

        {/* Test Purchase Event */}
        <div style={{ 
          background: 'white', 
          padding: '1.5rem', 
          borderRadius: '8px', 
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{ marginBottom: '1rem', color: '#1f2937' }}>💰 Test Purchase Event</h3>
          <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
            Fires a Purchase event directly without going through the payment flow.
          </p>
          <button 
            onClick={testPurchaseEvent}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Fire Purchase Event
          </button>
        </div>

        {/* Test Login Event */}
        <div style={{ 
          background: 'white', 
          padding: '1.5rem', 
          borderRadius: '8px', 
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{ marginBottom: '1rem', color: '#1f2937' }}>🔐 Test Login Event</h3>
          <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
            Fires a Login event directly to test login tracking.
          </p>
          <button 
            onClick={testLoginEvent}
            style={{
              background: '#8b5cf6',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Fire Login Event
          </button>
        </div>

        {/* Test Lead Event */}
        <div style={{ 
          background: 'white', 
          padding: '1.5rem', 
          borderRadius: '8px', 
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{ marginBottom: '1rem', color: '#1f2937' }}>📝 Test Lead Event</h3>
          <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
            Fires a Lead event for registration attempts.
          </p>
          <button 
            onClick={testLeadEvent}
            style={{
              background: '#f59e0b',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Fire Lead Event
          </button>
        </div>
      </div>

      <div style={{ 
        background: '#fef3c7', 
        padding: '1.5rem', 
        borderRadius: '8px', 
        marginTop: '2rem',
        border: '1px solid #f59e0b'
      }}>
        <h3 style={{ marginBottom: '1rem', color: '#92400e' }}>📋 Testing Instructions</h3>
        <ol style={{ color: '#92400e', lineHeight: '1.6' }}>
          <li><strong>Open Browser Developer Tools</strong> (F12) and go to the Console tab</li>
          <li><strong>Click "Open Payment Success Page"</strong> to test the full payment flow</li>
          <li><strong>Watch the console</strong> for Meta Pixel debug messages</li>
          <li><strong>Check Meta Events Manager</strong> after 1-2 hours for the events</li>
          <li><strong>Use individual event buttons</strong> to test specific events</li>
        </ol>
      </div>
    </div>
  );
};

// Component to display seasons with expand/collapse for many seasons
const SeasonBadgesList = ({ seasons, selectedSeason, teamId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const MAX_VISIBLE_SEASONS = 3;
  const visibleSeasons = isExpanded ? seasons : seasons.slice(0, MAX_VISIBLE_SEASONS);
  const hasMore = seasons.length > MAX_VISIBLE_SEASONS;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
      {visibleSeasons.map((season, idx) => {
        // Show checkmark for the selected filter season, not the active season
        const isSelectedSeason = selectedSeason && season.season_id === selectedSeason;
        // Highlight the original registration season
        const isOriginalRegistration = season.is_original_registration;
        return (
          <span
            key={`${teamId}-${season.season_id || 'unknown'}-${idx}`}
            style={{
              fontSize: '0.75rem',
              padding: '0.125rem 0.5rem',
              borderRadius: '4px',
              background: isSelectedSeason ? '#dbeafe' : 
                         isOriginalRegistration ? '#dcfce7' : // Green for original registration
                         season.season_status === 'completed' ? '#f3f4f6' : '#fef3c7',
              color: isSelectedSeason ? '#1e40af' : 
                     isOriginalRegistration ? '#166534' : // Dark green for original
                     season.season_status === 'completed' ? '#6b7280' : '#92400e',
              fontWeight: isOriginalRegistration ? '600' : '500',
              display: 'inline-block',
              marginBottom: '0.125rem',
              border: isOriginalRegistration ? '1px solid #16a34a' : 'none'
            }}
            title={`${isOriginalRegistration ? '⭐ Original Registration - ' : ''}Registered: ${season.registered_at ? new Date(season.registered_at).toLocaleDateString() : 'N/A'}`}
          >
            {isOriginalRegistration && '⭐ '}
            {season.season_name}
            {isSelectedSeason && ' ✓'}
          </span>
        );
      })}
      {hasMore && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          style={{
            fontSize: '0.7rem',
            padding: '0.125rem 0.375rem',
            borderRadius: '4px',
            border: '1px solid #d1d5db',
            background: '#f9fafb',
            color: '#6b7280',
            cursor: 'pointer',
            marginTop: '0.125rem',
            alignSelf: 'flex-start'
          }}
          title={isExpanded ? 'Show less' : `Show ${seasons.length - MAX_VISIBLE_SEASONS} more`}
        >
          {isExpanded ? '▲ Show less' : `▼ +${seasons.length - MAX_VISIBLE_SEASONS} more`}
        </button>
      )}
    </div>
  );
};

const AdminDashboardPage = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [upcomingMatchesOpen, setUpcomingMatchesOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { adminUser, logout, isLoading } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant } = useParams(); // Extract tenant from URL (e.g., /switzerland/admin/dashboard or /kamaanclub/admin/dashboard)
  const queryClient = useQueryClient();
  // When tenant is in URL, block rendering tenant-scoped content until we've set X-Tenant-ID to that tenant.
  // Otherwise useQueries run with the previous tenant (e.g. Switzerland) when opening from Super Admin.
  const [tenantContextReady, setTenantContextReady] = useState(() => !tenant);

  // Resolve tenant from URL before any tenant-scoped API calls run.
  // When coming from Super Admin "Open admin", use tenant from location.state so we never show wrong tenant.
  useEffect(() => {
    console.log('[Tenant] Dashboard: sync effect ran', {
      tenantFromUrl: tenant,
      hasAdminUser: !!adminUser,
      locationState: location.state,
      tenantFromSuperAdmin: location.state?.tenantFromSuperAdmin,
    });
    if (!tenant) {
      console.log('[Tenant] Dashboard: no tenant in URL → tenantContextReady = true');
      setTenantContextReady(true);
      return;
    }
    if (!adminUser) {
      console.log('[Tenant] Dashboard: no adminUser yet, skipping');
      return;
    }

    const fromState = location.state?.tenantFromSuperAdmin;
    const stateMatches = fromState && (fromState.slug === tenant || fromState.id === tenant);
    console.log('[Tenant] Dashboard: fromState check', { fromState, tenant, stateMatches });
    if (fromState && stateMatches) {
      console.log('[Tenant] Dashboard: using tenant FROM NAVIGATION STATE', fromState);
      setAdminTenantInfo({ id: fromState.id, slug: fromState.slug, name: fromState.name, type: fromState.type });
      setTenantContextReady(true);
      queryClient.invalidateQueries();
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    const current = getAdminTenantInfo();
    console.log('[Tenant] Dashboard: getTenantInfo() from localStorage', current);

    // Always resolve by URL slug against THIS environment's API.
    // Slug can match (switzerland) while UUID differs between staging and live —
    // if we skip the fetch when only the slug matches, admin APIs filter the wrong tenant
    // and lists (e.g. Clubs) look empty.
    console.log('[Tenant] Dashboard: fetching tenant-by-slug', tenant);
    setTenantContextReady(false);
    adminTenantBySlugAPI
      .get(tenant)
      .then((res) => {
        console.log('[Tenant] Dashboard: tenant-by-slug response', {
          success: res?.success,
          data: res?.data,
        });
        if (res && res.success && res.data) {
          const next = {
            id: res.data.id,
            slug: res.data.slug,
            name: res.data.name,
            type: res.data.type,
          };
          const idChanged = current?.id && current.id !== next.id;
          setAdminTenantInfo(next);
          setTenantContextReady(true);
          if (idChanged || !current?.id) {
            queryClient.invalidateQueries();
          }
        } else {
          setTenantContextReady(true);
        }
      })
      .catch((err) => {
        console.warn(
          '[Tenant] Dashboard: tenant-by-slug failed',
          err?.response?.status,
          err?.response?.data
        );
        // Fall back to cached tenant only if slug already matches URL
        if (current && current.slug === tenant) {
          setTenantContextReady(true);
        } else {
          setTenantContextReady(true);
        }
      });
  }, [tenant, adminUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle return from Stripe Connect onboarding (success or refresh). Settings panel may not be mounted, so handle here.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stripeParam = params.get('stripe');
    if (stripeParam === 'success') {
      toast.success('Stripe account connected successfully.');
      queryClient.invalidateQueries('adminTenantSettings');
      setActiveSection('settings-general');
      setSettingsOpen(true);
      navigate(location.pathname, { replace: true });
    } else if (stripeParam === 'refresh') {
      toast('Complete Stripe setup or try again.', { icon: '🔄' });
      queryClient.invalidateQueries('adminTenantSettings');
      setActiveSection('settings-general');
      setSettingsOpen(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate, queryClient]);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth <= 768 && sidebarOpen) {
        if (!event.target.closest('aside') && !event.target.closest('button[aria-label="Toggle menu"]')) {
          setSidebarOpen(false);
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen]);
  
  // Close sidebar when section changes on mobile
  useEffect(() => {
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  }, [activeSection]);

  // Permission checks: Super Admin or has the given action for section (used for menu + section actions)
  const canReadSection = (sectionKey) => {
    if (!adminUser) return false;
    if (adminUser.isSuperAdmin) return true;
    const perms = adminUser.permissions;
    if (perms && perms[sectionKey] && perms[sectionKey].read === true) return true;
    // New section: older JWT/localStorage may omit settings_tournaments — allow if they can use other Settings
    if (
      sectionKey === 'settings_tournaments' &&
      perms &&
      (perms.settings_general?.read || perms.settings_seasons?.read || perms.settings_promo_codes?.read)
    ) {
      return true;
    }
    if (
      sectionKey === 'organised_events' &&
      perms &&
      (perms.settings_tournaments?.read || perms.settings_general?.read)
    ) {
      return true;
    }
    return false;
  };
  const canCreateSection = (sectionKey) => {
    if (!adminUser) return false;
    if (adminUser.isSuperAdmin) return true;
    const perms = adminUser.permissions;
    return perms && perms[sectionKey] && perms[sectionKey].create === true;
  };
  const canEditSection = (sectionKey) => {
    if (!adminUser) return false;
    if (adminUser.isSuperAdmin) return true;
    const perms = adminUser.permissions;
    return perms && perms[sectionKey] && perms[sectionKey].edit === true;
  };
  const canDeleteSection = (sectionKey) => {
    if (!adminUser) return false;
    if (adminUser.isSuperAdmin) return true;
    const perms = adminUser.permissions;
    return perms && perms[sectionKey] && perms[sectionKey].delete === true;
  };

  // Upcoming Matches submenu items (defined early to use in useEffect)
  const upcomingMatchesItems = [
    { id: 'scheduled-matches', icon: '📅', label: 'Scheduled Matches', permissionSection: 'scheduled_matches' },
    { id: 'matches', icon: '⚽', label: 'By Canton', permissionSection: 'matches' },
  ];

  const settingsItems = [
    { id: 'settings-general', icon: '⚙️', label: 'General', permissionSection: 'settings_general' },
    { id: 'settings-clubs', icon: '🏟️', label: 'Clubs', permissionSection: 'settings_general' },
    { id: 'settings-promo-codes', icon: '🎟️', label: 'Promo Codes', permissionSection: 'settings_promo_codes' },
    { id: 'settings-registration-products', icon: '🧢', label: 'Registration products', permissionSection: 'settings_registration_products' },
    { id: 'settings-tournaments', icon: '🏆', label: 'Tournaments', permissionSection: 'settings_tournaments' },
    { id: 'settings-email', icon: '✉️', label: 'Email', permissionSection: 'settings_general' },
    { id: 'admin-users-and-roles', icon: '👥', label: 'Users & Roles', permissionSection: 'admin_users_and_roles' },
    { id: 'audit-log', icon: '📋', label: 'Audit log', permissionSection: 'audit_log' },
  ];

  // Auto-open upcoming matches accordion if one of its items is active
  useEffect(() => {
    if (upcomingMatchesItems.some(item => activeSection === item.id)) {
      setUpcomingMatchesOpen(true);
    }
  }, [activeSection]); // upcomingMatchesItems is a constant array, safe to omit from deps

  useEffect(() => {
    if (settingsItems.some(item => activeSection === item.id)) {
      setSettingsOpen(true);
    }
  }, [activeSection]);

  const menuItems = [
    { id: 'overview', icon: '📊', label: 'Overview', permissionSection: 'stats' },
    { id: 'analytics-hub', icon: '📉', label: 'Analytics', permissionSection: 'stats', isAnalyticsHub: true },
    { id: 'teams', icon: '👥', label: 'Teams', permissionSection: 'teams' },
    { id: 'team-provisioning', icon: '🧩', label: 'Team & player setup', permissionSection: 'teams' },
    { id: 'registration-orders', icon: '📦', label: 'Orders', permissionSection: 'settings_registration_products' },
    { id: 'shop', icon: '🛍️', label: 'Shop', permissionSection: 'shop' },
    { id: 'organised-events', icon: '🎫', label: 'Events', permissionSection: 'organised_events' },
    { id: 'divisions', icon: '🏆', label: 'Divisions', permissionSection: 'divisions' },
    { id: 'league-management', icon: '🎯', label: 'League Management', permissionSection: 'league_management' },
    { id: 'promotion-system', icon: '↕️', label: 'Promotion system', permissionSection: 'promotion_relegation' },
    { id: 'teammate-finder', icon: '🔍', label: 'Find Teammate Profiles', permissionSection: 'teammate_finder' },
    { id: 'players', icon: '👤', label: 'Players', permissionSection: 'teams', showOnlyForCountryTenant: true },
  ];

  const adminTenantInfo = getAdminTenantInfo();
  const isCountryTenant = adminTenantInfo?.type === 'country';
  const filteredMenuItems = menuItems.filter((item) => {
    if (item.isAnalyticsHub) {
      if (!canAccessAnalyticsHub(canReadSection)) return false;
    } else if (!canReadSection(item.permissionSection)) {
      return false;
    }
    if (item.showOnlyForCountryTenant && !isCountryTenant) return false;
    return true;
  });
  const filteredUpcomingMatchesItems = upcomingMatchesItems.filter((item) => canReadSection(item.permissionSection));
  const filteredSettingsItems = settingsItems.filter((item) => canReadSection(item.permissionSection));

  // When permissions load, if current section is not allowed, switch to first allowed
  useEffect(() => {
    if (!adminUser || adminUser.isSuperAdmin) return;
    if (activeSection === 'analytics-hub') {
      if (!canAccessAnalyticsHub(canReadSection)) {
        const first = filteredMenuItems[0] || filteredUpcomingMatchesItems[0] || filteredSettingsItems[0];
        if (first) setActiveSection(first.id);
        else setActiveSection('overview');
      }
      return;
    }
    const permissionKey = SECTION_TO_PERMISSION[activeSection] || activeSection;
    if (!canReadSection(permissionKey)) {
      const first = filteredMenuItems[0] || filteredUpcomingMatchesItems[0] || filteredSettingsItems[0];
      if (first) setActiveSection(first.id);
      else setActiveSection('overview');
    }
  }, [adminUser?.permissions, adminUser?.isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Standalone Settings → Phases was removed; phases are managed under Settings → Tournaments (seasons).
  useEffect(() => {
    if (activeSection === 'settings-seasons') setActiveSection('settings-tournaments');
  }, [activeSection]);

  // Legacy sidebar sections → Analytics / Promotion hubs (keep handoff tab)
  useEffect(() => {
    const redirect = LEGACY_SECTION_REDIRECT[activeSection];
    if (!redirect) return;
    setAdminNavHandoff({
      section: redirect.section,
      ...(redirect.analyticsHubTab ? { analyticsHubTab: redirect.analyticsHubTab } : {}),
      ...(redirect.promotionHubTab ? { promotionHubTab: redirect.promotionHubTab } : {}),
    });
    setActiveSection(redirect.section);
  }, [activeSection]);

  // Pagination state for teams
  const [currentPage, setCurrentPage] = useState(1);
  const [teamsPerPage] = useState(20); // Items per page

  // Search and filter state for teams
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedCanton, setSelectedCanton] = useState('');
  const [preferredPhaseId, setPreferredPhaseId] = useState('');
  const [registrationSourceFilter, setRegistrationSourceFilter] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('all'); // 'all', 'true', 'false'
  const [unassignedDivisionFilter, setUnassignedDivisionFilter] = useState(false);

  const {
    campaignId: teamsCampaignId,
    seasonId: selectedSeason,
    setSeasonId: setSelectedSeason,
    onCampaignChange: onTeamsCampaignChange,
    onPhaseChange: onTeamsPhaseChange,
    seasonOptions: teamsSeasonOptions,
    phaseOptions: teamsPhaseOptions,
    allPhases: teamsAllPhases,
    allowAll: teamsAllowAll,
  } = useAdminSeasonPhaseFilter({
    allowAll: true,
    autoDefault: true,
    enabled: !!adminUser,
    preferredPhaseId,
  });

  /**
   * Additive navigation with optional filter handoff into existing screens.
   * Does not change default behaviour when opening sections from the sidebar without filters.
   */
  const navigateAdmin = (section, filters = {}) => {
    const f = filters && typeof filters === 'object' ? filters : {};
    const legacy = LEGACY_SECTION_REDIRECT[section];
    const targetSection = legacy ? legacy.section : section;
    setAdminNavHandoff({
      section: targetSection,
      ...f,
      ...(legacy?.analyticsHubTab ? { analyticsHubTab: legacy.analyticsHubTab } : {}),
      ...(legacy?.promotionHubTab ? { promotionHubTab: legacy.promotionHubTab } : {}),
      // Preserve deep-dive inner tab when navigating to Breakdowns
      ...(f.analyticsTab && (!legacy || legacy.analyticsHubTab === 'deep-dive')
        ? { analyticsTab: f.analyticsTab }
        : {}),
    });

    if (targetSection === 'teams' || section === 'teams') {
      if (f.seasonId !== undefined) {
        const sid = f.seasonId ? String(f.seasonId) : '';
        setPreferredPhaseId(sid);
        if (!sid) setSelectedSeason('');
      }
      if (f.canton !== undefined) setSelectedCanton(f.canton || '');
      if (f.isActive !== undefined) setIsActiveFilter(f.isActive || 'all');
      if (f.unassignedDivision !== undefined) setUnassignedDivisionFilter(!!f.unassignedDivision);
      setCurrentPage(1);
    }

    setActiveSection(targetSection);
  };

  // If Teams was opened with a handoff (or remount), apply any remaining teams filters once.
  useEffect(() => {
    if (activeSection !== 'teams') return;
    const h = consumeAdminNavHandoff();
    if (!h || (h.section && h.section !== 'teams')) return;
    if (h.seasonId !== undefined) {
      const sid = h.seasonId ? String(h.seasonId) : '';
      setPreferredPhaseId(sid);
      if (!sid) setSelectedSeason('');
    }
    if (h.canton !== undefined) setSelectedCanton(h.canton || '');
    if (h.isActive !== undefined) setIsActiveFilter(h.isActive || 'all');
    if (h.unassignedDivision !== undefined) setUnassignedDivisionFilter(!!h.unassignedDivision);
    setCurrentPage(1);
  }, [activeSection]);

  // Edit team state
  const [editingTeam, setEditingTeam] = useState(null);
  const [editFormData, setEditFormData] = useState({ team_name: '', canton: '', division_id: '' });
  const [savingTeamEdit, setSavingTeamEdit] = useState(false);
  const [availableDivisions, setAvailableDivisions] = useState([]);

  // Fetch seasons for balancing tab and teams filter
  const { data: seasonsData } = useQuery(
    'admin-seasons',
    () => adminSeasonsAPI.getAll(),
    {
      enabled: !!adminUser,
      retry: false
    }
  );

  // Lock must follow the same season as moveTeam / TDA: the Teams filter, not the team's "latest" registration
  // (useDivisionLockStatus(teamId) alone used ORDER BY registered_at on the server and could block the wrong teams).
  const { isLocked: isDivisionLocked } = useDivisionLockStatus(
    selectedSeason ? null : editingTeam?.id || null,
    selectedSeason || null
  );

  // Divisions for team edit dropdown: must respect selected season (same rules as League Management).
  // Without season_id, the API returns every division in the DB (e.g. Mid-Season Group A/B + Final Sprint).
  // With season_id, group splits from other seasons are hidden and parent rows without active splits are shown.
  const { data: divisionsQueryData } = useQuery(
    ['admin-divisions', selectedSeason],
    () =>
      divisionsAPI.getAll({
        limit: 1000,
        ...(selectedSeason ? { season_id: selectedSeason } : {}),
      }),
    {
      enabled: !!adminUser,
      retry: false,
      onError: (error) => {
        console.error('❌ Error fetching divisions:', error);
      },
    }
  );
  const allDivisions = useMemo(() => {
    const raw =
      divisionsQueryData?.data?.divisions ||
      divisionsQueryData?.divisions ||
      [];
    return Array.isArray(raw) ? raw : [];
  }, [divisionsQueryData]);

  // Fetch admin overview data
  // Disable auto-refresh when editing team to prevent overwriting user changes
  const { data: overviewData, isLoading: overviewLoading, error: overviewError, refetch: refetchOverview } = useQuery(
    'adminOverview',
    () => adminStatsAPI.getOverview(),
    {
      enabled: !!adminUser,
      refetchInterval: editingTeam ? false : 60000, // Refetch every 60 seconds, disabled when editing
      staleTime: 0, // Always consider data stale
      cacheTime: 0, // Don't cache data
    }
  );


  // Season filter state for overview (currently unused - kept for potential future use)
  // eslint-disable-next-line no-unused-vars
  const [selectedOverviewSeason, setSelectedOverviewSeason] = useState('');

  // Fetch admin teams data with pagination and filters
  const { data: teamsData, isLoading: teamsLoading, error: teamsError, refetch: refetchAdminTeams } = useQuery(
    ['adminTeams', currentPage, teamsPerPage, searchTerm, selectedDivision, selectedCanton, teamsCampaignId, selectedSeason, registrationSourceFilter, isActiveFilter, unassignedDivisionFilter],
    () => adminTeamsAPI.getAll({ 
      page: currentPage, 
      limit: teamsPerPage,
      search: searchTerm,
      division: selectedDivision,
      canton: selectedCanton,
      season_id: selectedSeason || undefined,
      registration_source: registrationSourceFilter || undefined,
      is_active: isActiveFilter !== 'all' ? isActiveFilter : undefined,
      unassigned_division: unassignedDivisionFilter || undefined
    }),
    {
      enabled: !!adminUser && activeSection === 'teams',
      keepPreviousData: true, // Keep previous data while fetching new page
      staleTime: 0, // Force fresh data
      cacheTime: 0, // Don't cache
      onSuccess: (data) => {
        console.log('🔍 Teams data received:', data);
        console.log('🔍 Teams data.data:', data.data);
        console.log('🔍 Teams data.data.teams:', data.data?.teams);
        console.log('🔍 Teams data.data.teams length:', data.data?.teams?.length);
        console.log('🔍 Divisions filter:', data.data?.filters?.divisions);
        console.log('🔍 Cantons filter:', data.data?.filters?.cantons);
      },
      onError: (error) => {
        console.error('❌ Teams data error:', error);
      }
    }
  );

  // Reset to page 1 when switching to teams section
  useEffect(() => {
    if (activeSection === 'teams') {
      setCurrentPage(1);
    }
  }, [activeSection]);

  // Reset to page 1 when search or filters change
  useEffect(() => {
    if (activeSection === 'teams') {
      setCurrentPage(1);
    }
  }, [searchTerm, selectedDivision, selectedCanton, selectedSeason, activeSection, unassignedDivisionFilter]);

  // Filter divisions when canton changes in edit form
  useEffect(() => {
    if (editFormData.canton) {
      setAvailableDivisions(
        withCurrentDivision(divisionsForEditCanton(allDivisions, editFormData.canton), editingTeam)
      );
    } else {
      setAvailableDivisions(withCurrentDivision([], editingTeam));
    }
  }, [editFormData.canton, allDivisions, editingTeam]);


  useEffect(() => {
    if (!isLoading && !adminUser) {
      navigate('/admin/login');
    }
  }, [adminUser, isLoading, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      // Already on home page, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Navigate to home page and scroll to top
      navigate('/');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  // Team details modal state
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showTeamDetails, setShowTeamDetails] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [logoActionInProgress, setLogoActionInProgress] = useState(false);
  const teamLogoInputRef = useRef(null);

  // Team action handlers — merge season-scoped TDA from details API (list row already uses COALESCE; /details used to only use teams.division_id).
  const handleEditTeam = async (team) => {
    let details = null;
    let effective = { ...team };
    if (selectedSeason) {
      try {
        const response = await adminTeamsAPI.getTeamDetails(team.id, { seasonId: selectedSeason });
        if (response?.success && response.data?.team) {
          details = response.data.team;
          effective = {
            ...team,
            ...details,
            canton: resolveEditPlayingCanton(team, details, allDivisions),
            division_id: firstNonEmpty(details.division_id, team.division_id),
            division_name: firstNonEmpty(details.division_name, team.division_name),
          };
        }
      } catch (e) {
        console.warn('Season-scoped team details failed; using list row for edit form', e);
      }
    }

    console.log('📝 ========== EDITING TEAM ==========');
    console.log('📝 Team ID:', effective.id);
    console.log('📝 Team Name:', effective.team_name);
    console.log('📝 Selected Season:', selectedSeason);
    console.log('📝 Team division_id (season-aware for selected season):', effective.division_id);
    console.log('📝 Team division_name (season-aware for selected season):', effective.division_name);
    console.log('📝 Team canton:', effective.canton);
    console.log('📝 Full team object:', effective);

    setEditingTeam(effective);

    const teamCanton = resolveEditPlayingCanton(team, details, allDivisions);
    const teamDivisionId = firstNonEmpty(effective.division_id, team.division_id);

    setEditFormData({
      team_name: effective.team_name || '',
      canton: teamCanton,
      division_id: teamDivisionId
    });

    console.log('📝 Form initialized with:');
    console.log('   - Canton:', teamCanton);
    console.log('   - Division ID:', teamDivisionId || '(none — not assigned in this season)');
    console.log('📝 ====================================');

    // Filter divisions for this team's canton (BS → Basel, etc.)
    if (teamCanton) {
      setAvailableDivisions(
        withCurrentDivision(divisionsForEditCanton(allDivisions, teamCanton), {
          ...effective,
          canton: teamCanton,
          division_id: teamDivisionId,
        })
      );
    } else {
      setAvailableDivisions(withCurrentDivision([], effective));
    }

    console.log('🔍 Form data set to:', {
      team_name: effective.team_name,
      canton: effective.canton,
      division_id: effective.division_id || ''
    });
  };

  const handleSaveTeamEdit = async () => {
    if (!editingTeam) return;
    
    setSavingTeamEdit(true);
    try {
      console.log('💾 ========== SAVING TEAM UPDATE ==========');
      console.log('💾 Team ID:', editingTeam.id);
      console.log('💾 Team Name:', editingTeam.team_name);
      console.log('💾 Selected Season:', selectedSeason);
      console.log('💾 Current division_id (from team object):', editingTeam.division_id);
      console.log('💾 Current division_name (from team object):', editingTeam.division_name);
      console.log('💾 Form data:', editFormData);
      
      // All seasons selected: only global team name (no season_id — updates teams.team_name for login + dashboards).
      if (!selectedSeason) {
        if (editFormData.team_name === editingTeam.team_name) {
          toast.error('Change the team name before saving, or cancel.');
          return;
        }
        const response = await adminTeamsAPI.update(editingTeam.id, {
          team_name: editFormData.team_name,
        });
        if (!response.success) {
          throw new Error(response.message || 'Failed to update team name');
        }
        toast.success('Team name updated globally. Teams will see it on login and dashboard.');
        await queryClient.invalidateQueries(['adminTeams', currentPage, teamsPerPage, searchTerm, selectedDivision, selectedCanton, teamsCampaignId, selectedSeason, registrationSourceFilter, isActiveFilter, unassignedDivisionFilter]);
        await queryClient.invalidateQueries(['adminTeams']);
        await queryClient.invalidateQueries(['teamDetails', editingTeam.id]);
        await refetchAdminTeams();
        setEditingTeam(null);
        setEditFormData({ team_name: '', canton: '', division_id: '' });
        return;
      }

      const targetSeasonId = selectedSeason;

      // If division_id is a string like 'elite', 'advanced', etc., we need to find the actual division ID
      let finalDivisionId = editFormData.division_id;
      
      if (editFormData.division_id && !editFormData.division_id.includes('-')) {
        // This is a standard division level, find the actual division ID
        const actualDivision = allDivisions.find(div => 
          div.canton === editFormData.canton && div.name.toLowerCase().replace(' ', '-') === editFormData.division_id
        );
        
        if (actualDivision) {
          finalDivisionId = actualDivision.id;
          console.log('💾 Found actual division ID:', actualDivision.id, 'for division:', editFormData.division_id);
        } else {
          console.log('⚠️ Could not find actual division ID for:', editFormData.division_id);
          // For now, set to null if we can't find the actual division
          finalDivisionId = null;
        }
      }
      
      // Check what changed (UUID/string-safe so null, "", and "uuid" match correctly)
      let divisionChanged = normAdminDivisionId(editingTeam.division_id) !== normAdminDivisionId(finalDivisionId);
      const teamNameChanged = editingTeam.team_name !== editFormData.team_name;
      let cantonChanged = !sameAdminCanton(editingTeam.canton, editFormData.canton);
      
      // When divisions are locked: only team name can be changed/saved. Canton and division are tied to leaderboards.
      if (isDivisionLocked) {
        if (divisionChanged || cantonChanged) {
          if (!teamNameChanged) {
            toast.error('Division and canton cannot be changed while divisions are locked. You can only change and save the team name.');
            return;
          }
          toast('Division and canton cannot be changed while locked. Saving team name only.', { icon: 'ℹ️' });
        }
        divisionChanged = false;
        cantonChanged = false; // do not save canton when lock is on
      }
      
      // If division changed, use moveTeam() to update both teams.division_id and team_division_assignments
      if (divisionChanged) {
        console.log('💾 Division changed, using moveTeam()');
        console.log('💾 Division change:', editingTeam.division_id, '→', finalDivisionId);
        console.log('💾 Season ID:', targetSeasonId);
        
        try {
          const moveResult = await adminLeagueManagementAPI.moveTeam(
            editingTeam.id,
            normAdminDivisionId(editingTeam.division_id),
            normAdminDivisionId(finalDivisionId),
            targetSeasonId,
            `Updated via Teams section - ${teamNameChanged ? 'name changed, ' : ''}${cantonChanged ? 'canton changed, ' : ''}division changed`
          );
          if (moveResult && typeof moveResult === 'object' && moveResult.success === false) {
            throw new Error(moveResult.message || 'Move team failed');
          }
          console.log('✅ Division assignment updated via moveTeam()');
        } catch (moveError) {
          console.error('❌ Error moving team:', moveError);
          throw new Error(`Failed to update division assignment: ${moveError.response?.data?.message || moveError.message}`);
        }
      }
      
      // Update team name and canton if changed (but not division - that's handled above)
      if (teamNameChanged || cantonChanged) {
        console.log('💾 Team name or canton changed, using update()');
        const updateData = {
          ...(teamNameChanged ? { team_name: editFormData.team_name } : {}),
          ...(isDivisionLocked || !cantonChanged ? {} : { canton: editFormData.canton }),
          season_id: targetSeasonId,
        };
        
        console.log('💾 Update payload:', updateData);
        console.log('💾 Season ID included:', targetSeasonId);
        
        const response = await adminTeamsAPI.update(editingTeam.id, updateData);
        console.log('💾 API Response:', response);
        
        if (!response.success) {
          console.error('❌ Team update failed:', response);
          throw new Error(response.message || 'Failed to update team');
        }
      }
      
      console.log('✅ Team update successful!');
      toast.success(divisionChanged ? 'Team moved to the new division. Refreshing the list…' : 'Team updated successfully.');
      
      // Invalidate and await refetch so the table shows the new division before the modal closes
      await queryClient.invalidateQueries(['adminTeams', currentPage, teamsPerPage, searchTerm, selectedDivision, selectedCanton, teamsCampaignId, selectedSeason, registrationSourceFilter, isActiveFilter, unassignedDivisionFilter]);
      await queryClient.invalidateQueries(['adminTeams']);
      await queryClient.invalidateQueries(['divisions-overview', targetSeasonId]);
      await queryClient.invalidateQueries(['divisions-by-level', targetSeasonId]);
      await queryClient.invalidateQueries(['teamDetails', editingTeam.id]);
      
      if (divisionChanged) {
        await queryClient.invalidateQueries('cantonDivisions');
        await queryClient.invalidateQueries('cantonTeams');
        await queryClient.invalidateQueries(['divisions-balancing', targetSeasonId]);
      }
      await refetchAdminTeams();
      
      console.log('💾 Invalidated queries for refresh');
      console.log('💾 ====================================');
      
      setEditingTeam(null);
      setEditFormData({ team_name: '', canton: '', division_id: '' });
    } catch (error) {
      console.error('❌ ========== ERROR UPDATING TEAM ==========');
      console.error('❌ Error:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error message:', error.message);
      console.error('❌ ====================================');
      toast.error(
        error.response?.data?.message || error.message || 'Error updating team. Please try again.',
        { duration: error.response?.data?.code === 'PLAYER_CANTON_DIVISION_CONFLICT' ? 8000 : 4000 }
      );
    } finally {
      setSavingTeamEdit(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingTeam(null);
    setEditFormData({ team_name: '', canton: '', division_id: '' });
  };

  // State for season selection modal when enabling/disabling teams
  const [showSeasonSelectionModal, setShowSeasonSelectionModal] = useState(false);
  const [teamToEnable, setTeamToEnable] = useState(null);
  const [selectedSeasonForEnable, setSelectedSeasonForEnable] = useState('');
  const [isDisablingTeam, setIsDisablingTeam] = useState(false); // Track if we're disabling or enabling

  // Reuse existing seasonsData from line 1267 - no need for duplicate query

  const handleToggleTeamStatus = async (team) => {
    // Use season-specific status if available, otherwise use global status
    const currentStatus = team.season_is_active !== undefined ? team.season_is_active : team.is_active;
    const newStatus = !currentStatus;
    // Action is based on currentStatus: if active, we're disabling; if disabled, we're reactivating
    const action = currentStatus ? 'disable' : 'reactivate';
    const actionVerb = currentStatus ? 'disabled' : 'reactivated';
    
    // Get the selected season name
    const selectedSeasonName = selectedSeason && seasonsData?.data?.seasons
      ? seasonsData.data.seasons.find(s => s.id === selectedSeason)?.name
      : null;
    
    // If a season is already selected, use it directly (no modal needed)
    if (selectedSeason) {
      const confirmMessage = `Are you sure you want to ${action} "${team.team_name}" for ${selectedSeasonName || 'the selected phase'}? This will only affect this phase - their data from other phases will remain intact.`;
      if (!window.confirm(confirmMessage)) {
        return;
      }

      try {
        const response = await adminTeamsAPI.update(team.id, { 
          team_name: team.team_name,
          canton: team.canton,
          is_active: newStatus,
          season_id: selectedSeason // Use the currently selected season
        });
        
        if (response.success) {
          // Refresh teams data - invalidate and refetch to ensure fresh data
          queryClient.invalidateQueries(['adminTeams']);
          // Wait for refetch to complete before showing alert
          await queryClient.refetchQueries(['adminTeams']);
          setCurrentPage(1);
          alert(`Team ${actionVerb} for ${selectedSeasonName || 'the selected phase'}. Their data from other phases will remain intact.`);
        }
      } catch (error) {
        console.error(`Error ${action}ing team:`, error);
        alert(`Error ${action}ing team: ${error.response?.data?.message || error.message || 'Please try again.'}`);
      }
    } else {
      // No season selected - show modal to choose season or global disable
      setTeamToEnable(team);
      setIsDisablingTeam(!newStatus); // true if disabling, false if enabling
      setShowSeasonSelectionModal(true);
      // Reset season selection
      setSelectedSeasonForEnable('');
    }
  };

  const handleEnableTeamWithSeason = async () => {
    if (!selectedSeasonForEnable) {
      alert(`Please select a phase to ${isDisablingTeam ? 'disable' : 'activate'} this team in.`);
      return;
    }

    if (!teamToEnable) {
      return;
    }

    const action = isDisablingTeam ? 'disable' : 'activate';
    const newStatus = !isDisablingTeam; // true if enabling, false if disabling

    try {
      const response = await adminTeamsAPI.update(teamToEnable.id, { 
        team_name: teamToEnable.team_name,
        canton: teamToEnable.canton,
        is_active: newStatus,
        season_id: selectedSeasonForEnable // Pass season_id for season-aware enable/disable
      });
      
      if (response.success) {
        // Close modal and refresh
        setShowSeasonSelectionModal(false);
        setTeamToEnable(null);
        setSelectedSeasonForEnable('');
        setIsDisablingTeam(false);
        // Refresh teams data without reloading the page
        queryClient.invalidateQueries(['adminTeams']);
        // CRITICAL: Also invalidate all division-related queries to reflect team status changes
        queryClient.invalidateQueries(['cantonTeams']);
        queryClient.invalidateQueries(['cantonDivisions']);
        queryClient.invalidateQueries(['divisions-overview']);
        queryClient.invalidateQueries(['divisionTeams']);
        queryClient.invalidateQueries(['cantonDivisions']);
        // Reset to first page if needed
        setCurrentPage(1);
        const actionVerb = newStatus ? 'disabled' : 'reactivated';
        alert(`Team ${actionVerb} for the selected phase. Their data from other phases will remain intact.`);
      }
    } catch (error) {
      console.error(`Error ${action}ing team:`, error);
      alert(`Error ${action}ing team: ${error.response?.data?.message || error.message || 'Please try again.'}`);
    }
  };

  const handleCancelSeasonSelection = () => {
    setShowSeasonSelectionModal(false);
    setTeamToEnable(null);
    setSelectedSeasonForEnable('');
    setIsDisablingTeam(false);
  };

  const handleViewTeamDetails = async (team) => {
    setSelectedTeam(team);
    setLoadingMembers(true);
    setShowTeamDetails(true);
    
    try {
      // Fetch team details including members and phone number (season_id: division from TDA)
      const response = await adminTeamsAPI.getTeamDetails(team.id, { seasonId: selectedSeason || undefined });
      if (response.success) {
        if (response.data.members) {
          setTeamMembers(response.data.members);
        }
        // Update selectedTeam with full details including phone_number
        if (response.data.team) {
          setSelectedTeam(prev => ({ ...prev, ...response.data.team }));
        }
      }
    } catch (error) {
      console.error('Error fetching team details:', error);
      setTeamMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const refreshTeamDetailsMembers = async () => {
    if (!selectedTeam?.id) return;
    try {
      const response = await adminTeamsAPI.getTeamDetails(selectedTeam.id, {
        seasonId: selectedSeason || undefined,
      });
      if (response.success) {
        if (response.data.members) setTeamMembers(response.data.members);
        if (response.data.team) {
          setSelectedTeam((prev) => ({ ...prev, ...response.data.team }));
        }
      }
    } catch (error) {
      console.error('Error refreshing team details:', error);
    }
  };

  const handleCloseTeamDetails = () => {
    setShowTeamDetails(false);
    setSelectedTeam(null);
    setTeamMembers([]);
  };

  const getTeamInitials = (name) => {
    if (!name || typeof name !== 'string') return 'TM';
    return name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'TM';
  };

  const handleTeamLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTeam?.id) return;
    e.target.value = '';
    setLogoActionInProgress(true);
    try {
      const res = await adminTeamsAPI.uploadLogo(selectedTeam.id, file);
      const isError = res && (res.success === false || res.error != null);
      if (!isError) {
        queryClient.invalidateQueries(['adminTeams']);
        queryClient.invalidateQueries(['teamDetails', selectedTeam.id]);
        try {
          const updated = await adminTeamsAPI.getTeamDetails(selectedTeam.id, { seasonId: selectedSeason || undefined });
          if (updated?.data?.team?.team_logo_url != null) {
            setSelectedTeam(prev => ({ ...prev, team_logo_url: updated.data.team.team_logo_url }));
          }
        } catch (_) { /* use cached/invalidated data on next open */ }
        toast.success('Team logo updated');
      } else {
        toast.error(res?.message || res?.data?.message || 'Failed to update logo');
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status >= 200 && status < 300) {
        queryClient.invalidateQueries(['adminTeams']);
        queryClient.invalidateQueries(['teamDetails', selectedTeam.id]);
        toast.success('Team logo updated');
      } else {
        toast.error(err?.response?.data?.message || 'Failed to update logo');
      }
    } finally {
      setLogoActionInProgress(false);
    }
  };

  const handleRemoveTeamLogo = async () => {
    if (!selectedTeam?.id) return;
    setLogoActionInProgress(true);
    try {
      const res = await adminTeamsAPI.removeLogo(selectedTeam.id);
      const isError = res && (res.success === false || res.error != null);
      if (!isError) {
        setSelectedTeam(prev => ({ ...prev, team_logo_url: null }));
        queryClient.invalidateQueries(['adminTeams']);
        queryClient.invalidateQueries(['teamDetails', selectedTeam.id]);
        toast.success('Team logo removed');
      } else {
        toast.error(res?.message || res?.data?.message || 'Failed to remove logo');
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status >= 200 && status < 300) {
        setSelectedTeam(prev => ({ ...prev, team_logo_url: null }));
        queryClient.invalidateQueries(['adminTeams']);
        queryClient.invalidateQueries(['teamDetails', selectedTeam.id]);
        toast.success('Team logo removed');
      } else {
        toast.error(err?.response?.data?.message || 'Failed to remove logo');
      }
    } finally {
      setLogoActionInProgress(false);
    }
  };

  // Lock body scroll when team details modal is open
  useEffect(() => {
    if (showTeamDetails) {
      // Store the current scroll position
      const scrollY = window.scrollY;
      // Get the scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      
      // Lock the body scroll without affecting fixed elements like sidebar
      // Use overflow hidden without changing position to avoid affecting fixed elements
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      
      // Also add padding to the sidebar to prevent it from shifting
      const sidebar = document.querySelector('aside');
      if (sidebar) {
        sidebar.style.paddingRight = `${scrollbarWidth}px`;
      }
      
      return () => {
        // Restore scroll when modal closes
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        if (sidebar) {
          sidebar.style.paddingRight = '';
        }
        window.scrollTo(0, scrollY);
      };
    }
  }, [showTeamDetails]);

  const handleExportTeamsData = async () => {
    try {
      // Fetch all teams data with members
      const response = await adminTeamsAPI.getAll({ limit: 10000 }); // Get all teams
      const teams = response.data.teams || [];
      
      if (teams.length === 0) {
        alert('No teams data to export');
        return;
      }

      console.log('🔍 Teams from API:', teams);

      // Fetch detailed data for each team including members
      const teamsWithMembers = await Promise.all(
        teams.map(async (team) => {
          try {
            const teamDetailsResponse = await adminTeamsAPI.getTeamDetails(team.id, { seasonId: selectedSeason || undefined });
            console.log(`🔍 Team ${team.id} details:`, teamDetailsResponse.data);
            return {
              ...team,
              email: teamDetailsResponse.data.team?.email || '',
              members: teamDetailsResponse.data.members || []
            };
          } catch (error) {
            console.error(`Error fetching details for team ${team.id}:`, error);
            return {
              ...team,
              email: '',
              members: []
            };
          }
        })
      );

      console.log('🔍 Teams with members:', teamsWithMembers);

      // Create CSV content with more detailed structure
      const csvHeaders = [
        'Team ID',
        'Team Name',
        'Team Email',
        'Canton',
        'Registration Canton',
        'Team Skill Level',
        'Division',
        'Member Count',
        'Status',
        'Joined Date',
        'Member 1 First Name',
        'Member 1 Last Name',
        'Member 1 Email',
        'Member 1 Skill Level',
        'Member 1 Instagram Handle',
        'Member 1 Home Club',
        'Member 2 First Name',
        'Member 2 Last Name',
        'Member 2 Email',
        'Member 2 Skill Level',
        'Member 2 Instagram Handle',
        'Member 2 Home Club'
      ];

      const csvRows = teamsWithMembers.map(team => [
        team.id,
        team.team_name || '',
        team.email || '',
        team.canton || '',
        team.registration_canton || '',
        team.team_skill_level || '',
        team.division_name || 'Unassigned',
        team.member_count || 0,
        team.is_active ? 'Active' : 'Inactive',
        team.created_at ? new Date(team.created_at).toLocaleDateString() : '',
        // Member 1 details
        team.members[0]?.first_name || '',
        team.members[0]?.last_name || '',
        team.members[0]?.email || '',
        team.members[0]?.skill_level || '',
        team.members[0]?.instagram_handle || '',
        team.members[0]?.home_club || '',
        // Member 2 details
        team.members[1]?.first_name || '',
        team.members[1]?.last_name || '',
        team.members[1]?.email || '',
        team.members[1]?.skill_level || '',
        team.members[1]?.instagram_handle || '',
        team.members[1]?.home_club || ''
      ]);

      // Convert to CSV format
      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `teams_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(`Exported ${teams.length} teams to CSV`);
    } catch (error) {
      console.error('Error exporting teams data:', error);
      alert('Error exporting teams data. Please try again.');
    }
  };

  const handleExportCompleteData = async () => {
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (!token) {
        alert('Please log in to export data');
        return;
      }

      const response = await fetch('/api/admin/teams/complete-export', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export data');
      }

      // Get the blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `complete-teams-data-${new Date().toISOString().split('T')[0]}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Complete data exported successfully');
    } catch (error) {
      console.error('Error exporting complete data:', error);
      alert('Error exporting complete data. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  const renderContent = () => {
    if (activeSection === 'analytics-hub') {
      if (!canAccessAnalyticsHub(canReadSection)) {
        return (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
            <p>You don&apos;t have access to this section.</p>
            <p style={{ fontSize: '0.875rem' }}>Contact your administrator to request access.</p>
          </div>
        );
      }
      return <AdminAnalyticsHub onNavigate={navigateAdmin} canReadSection={canReadSection} />;
    }

    const permissionKey = SECTION_TO_PERMISSION[activeSection] || activeSection;
    if (!canReadSection(permissionKey)) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
          <p>You don&apos;t have access to this section.</p>
          <p style={{ fontSize: '0.875rem' }}>Contact your administrator to request access.</p>
        </div>
      );
    }
    switch (activeSection) {
      case 'admin-users-and-roles':
        return <AdminUsersAndRolesSection />;
      case 'audit-log':
        return <AdminAuditLogPanel />;
      case 'players':
        return <AdminPlayersSection />;
      case 'overview':
        return (
          <div>
            <WelcomeSection>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3>Welcome to Admin Dashboard</h3>
                  <p>Manage your Helvetic Padel Series from here</p>
                </div>
                <button 
                  onClick={() => refetchOverview()} 
                  style={{
                    background: '#043663',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  🔄 Refresh Data
                </button>
              </div>
            </WelcomeSection>
            
            {overviewLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div>Loading overview data...</div>
              </div>
            ) : overviewError ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#d52b1e' }}>
                <div>Error loading data: {overviewError.message}</div>
              </div>
            ) : (
              <>
                <StatsGrid>
                  <StatCard>
                    <div className="stat-number">{overviewData?.data?.totalTeams || 0}</div>
                    <div className="stat-label">Total Teams</div>
                  </StatCard>
                  <StatCard>
                    <div className="stat-number">{overviewData?.data?.totalMembers || 0}</div>
                    <div className="stat-label">Active Players</div>
                  </StatCard>
                  <StatCard>
                    <div className="stat-number">{overviewData?.data?.totalDivisions || 0}</div>
                    <div className="stat-label">Divisions</div>
                  </StatCard>
                  <StatCard>
                    <div className="stat-number">{overviewData?.data?.totalMatches || 0}</div>
                    <div className="stat-label">Matches Scheduled</div>
                  </StatCard>
                </StatsGrid>

                {overviewData?.data && (
                  <>
                    {console.log('🔍 Admin Overview Data:', overviewData.data)}
                    {console.log('🔍 Teams by Canton:', overviewData.data.teamsByCanton)}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginTop: '2rem' }}>
                    {/* Teams by Canton */}
                    <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
                      <h4 style={{ margin: '0 0 1rem 0', color: '#1f2937', fontSize: '1.1rem', fontWeight: '600' }}>Teams by Canton</h4>
                      {overviewData.data.teamsByCanton?.length > 0 ? (
                        <div style={{ overflow: 'hidden' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                                <th style={{ 
                                  textAlign: 'left', 
                                  padding: '0.75rem 0', 
                                  fontWeight: '600', 
                                  color: '#374151',
                                  fontSize: '0.875rem'
                                }}>
                                  Canton
                                </th>
                                <th style={{ 
                                  textAlign: 'right', 
                                  padding: '0.75rem 0', 
                                  fontWeight: '600', 
                                  color: '#374151',
                                  fontSize: '0.875rem'
                                }}>
                                  Teams
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {overviewData.data.teamsByCanton.map((canton, index) => (
                                <tr key={index} style={{ 
                                  borderBottom: index < overviewData.data.teamsByCanton.length - 1 ? '1px solid #f3f4f6' : 'none'
                                }}>
                                  <td style={{ 
                                    padding: '0.75rem 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                  }}>
                                    <span style={{ 
                                      fontWeight: '500', 
                                      color: '#1f2937',
                                      fontSize: '0.9rem'
                                    }}>
                                      {canton.canton}
                                    </span>
                                    <span style={{ 
                                      fontSize: '0.75rem', 
                                      color: '#6b7280',
                                      background: '#f3f4f6',
                                      padding: '0.125rem 0.375rem',
                                      borderRadius: '4px'
                                    }}>
                                      {canton.canton_code}
                                    </span>
                                  </td>
                                  <td style={{ 
                                    textAlign: 'right',
                                    padding: '0.75rem 0'
                                  }}>
                                    <span style={{ 
                                      fontWeight: '700', 
                                      color: canton.total_teams > 0 ? '#043663' : '#9ca3af',
                                      fontSize: '1rem'
                                    }}>
                                      {canton.total_teams}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p style={{ color: '#6b7280', margin: 0 }}>No canton data available</p>
                      )}
                    </div>

                    {/* Recent Teams */}
                    <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
                      <h4 style={{ margin: '0 0 1rem 0', color: '#1f2937' }}>Recent Teams (7 days)</h4>
                      {overviewData.data.recentTeams?.length > 0 ? (
                        <div>
                          {overviewData.data.recentTeams.map((team, index) => (
                            <div key={index} style={{ padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6' }}>
                              <div style={{ fontWeight: '500' }}>{team.team_name}</div>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                {team.canton} • {new Date(team.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ color: '#6b7280', margin: 0 }}>No recent team registrations</p>
                      )}
                    </div>
                  </div>

                  </>
                )}

                {overviewData?.data?.unassignedTeams > 0 && (
                  <div style={{ 
                    background: '#fef3c7', 
                    border: '1px solid #f59e0b', 
                    borderRadius: '8px', 
                    padding: '1rem', 
                    marginTop: '1rem',
                    color: '#92400e'
                  }}>
                    <strong>⚠️ {overviewData.data.unassignedTeams} teams</strong> are not assigned to any division yet.
                  </div>
                )}
              </>
            )}
          </div>
        );
      
      case 'teams':
        return (
          <>
          <TeamsContainer>
            <TeamsHeader>
              <div>
                <TeamsTitle>Teams Management</TeamsTitle>
                {selectedSeason && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    marginTop: '0.5rem',
                    fontSize: '0.875rem',
                    color: '#6b7280'
                  }}>
                    <span>📅 Teams in this phase:</span>
                    <span style={{ 
                      fontWeight: 600, 
                      color: '#043663',
                      padding: '0.25rem 0.75rem',
                      background: '#dbeafe',
                      borderRadius: '4px'
                    }}>
                      {teamsAllPhases.find((s) => String(s.id) === String(selectedSeason))?.name
                        || seasonsData?.data?.find(s => s.id === selectedSeason)?.name
                        || 'Selected Phase'}
                    </span>
                  </div>
                )}
                {teamsData?.data?.pagination && (
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    Showing {teamsData.data.teams?.length || 0} of {teamsData.data.pagination.total} teams
                    {selectedSeason
                      ? ' (Phase 1: org only. Later phases: org + full roster carried from the previous phase — so Phase 3 carry includes Phase 1 and Phase 2 orgs who moved forward.)'
                      : ' (all phases)'}
                  </div>
                )}
                {selectedSeason && teamsData?.data?.registrationTypeBreakdown && (
                  <div style={{ 
                    display: 'flex', 
                    gap: '1rem', 
                    marginTop: '0.75rem',
                    flexWrap: 'nowrap',
                    fontSize: '0.875rem',
                    padding: '0.75rem 1rem',
                    background: '#f8fafc',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    alignItems: 'center'
                  }}>
                    {(() => {
                      const breakdown = teamsData.data.registrationTypeBreakdown;
                      const original = parseInt(breakdown.original_count || 0);
                      const autoRegistered = parseInt(breakdown.auto_registered_count || 0);
                      const reactivated = parseInt(breakdown.reactivated_count || 0);
                      const mislinked = parseInt(breakdown.mislinked_count || 0);
                      const total = parseInt(breakdown.total_count || 0);
                      return (
                        <>
                          <span style={{ 
                            color: '#166534', 
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)'
                          }}>
                            ⭐ <strong style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>{original}</strong> Original {total > 0 ? `(${((original / total) * 100).toFixed(1)}%)` : '(0%)'}
                          </span>
                          <span style={{ 
                            color: '#1e40af', 
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)'
                          }}>
                            ⬇️ <strong style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>{autoRegistered}</strong> From Previous {total > 0 ? `(${((autoRegistered / total) * 100).toFixed(1)}%)` : '(0%)'}
                          </span>
                          <span style={{ 
                            color: '#92400e', 
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)'
                          }}>
                            🔄 <strong style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>{reactivated}</strong> Reactivated {total > 0 ? `(${((reactivated / total) * 100).toFixed(1)}%)` : '(0%)'}
                          </span>
                          {mislinked > 0 ? (
                            <span
                              style={{
                                color: '#b91c1c',
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                              }}
                              title="Has a seat in this phase, but true first signup (⭐) is a later phase — likely backfilled TSR"
                            >
                              ⚠️ <strong style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>{mislinked}</strong> Mislinked{' '}
                              {total > 0 ? `(${((mislinked / total) * 100).toFixed(1)}%)` : '(0%)'}
                            </span>
                          ) : null}
                          <span style={{ 
                            color: '#6b7280', 
                            fontWeight: '600', 
                            marginLeft: 'auto',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)'
                          }}>
                            📊 Total: <strong style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)', color: '#1f2937' }}>{total}</strong> teams
                          </span>
                        </>
                      );
                    })()}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  <button 
                    onClick={() => window.location.reload()} 
                    style={{
                      padding: '4px 8px',
                      fontSize: '0.75rem',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    🔄 Refresh Data
                  </button>
                  <button 
                    onClick={handleExportTeamsData} 
                    disabled={teamsLoading || !teamsData?.data?.teams?.length}
                    style={{
                      padding: '4px 8px',
                      fontSize: '0.75rem',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: teamsLoading || !teamsData?.data?.teams?.length ? 'not-allowed' : 'pointer',
                      opacity: teamsLoading || !teamsData?.data?.teams?.length ? 0.5 : 1
                    }}
                  >
                    📊 Export Data
                  </button>
                  <button 
                    onClick={handleExportCompleteData} 
                    disabled={teamsLoading || !teamsData?.data?.teams?.length}
                    style={{
                      padding: '4px 8px',
                      fontSize: '0.75rem',
                      background: '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: teamsLoading || !teamsData?.data?.teams?.length ? 'not-allowed' : 'pointer',
                      opacity: teamsLoading || !teamsData?.data?.teams?.length ? 0.5 : 1
                    }}
                  >
                    📋 Complete Data
                  </button>
                </div>
              </div>
              <SearchContainer>
                <SearchInput 
                  type="text" 
                  placeholder="Search teams..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <AdminSeasonPhaseFilters
                  campaignId={teamsCampaignId}
                  seasonId={selectedSeason}
                  seasonOptions={teamsSeasonOptions}
                  phaseOptions={teamsPhaseOptions}
                  onCampaignChange={onTeamsCampaignChange}
                  onPhaseChange={onTeamsPhaseChange}
                  allowAll={teamsAllowAll}
                />
                <FilterSelect
                  value={selectedDivision}
                  onChange={(e) => setSelectedDivision(e.target.value)}
                >
                  <option value="">All Divisions</option>
                  {teamsData?.data?.filters?.divisions?.map(div => (
                    <option key={div.id} value={div.id}>{div.name}</option>
                  ))}
                </FilterSelect>
                <FilterSelect
                  value={selectedCanton}
                  onChange={(e) => setSelectedCanton(e.target.value)}
                >
                  <option value="">All Cantons</option>
                  {teamsData?.data?.filters?.cantons?.map(canton => (
                    <option key={canton} value={canton}>{canton}</option>
                  ))}
                </FilterSelect>
                <FilterSelect
                  value={registrationSourceFilter || 'all'}
                  onChange={(e) => {
                    const value = e.target.value === 'all' ? '' : e.target.value;
                    setRegistrationSourceFilter(value);
                  }}
                >
                  <option value="all">All Sources</option>
                  <option value="meta">📱 Meta Ads</option>
                  <option value="google">🔍 Google Ads</option>
                  <option value="direct">🔗 Direct</option>
                  <option value="referral">👥 Referral</option>
                  <option value="email">📧 Email</option>
                  <option value="organic">🌱 Organic</option>
                </FilterSelect>
                <FilterSelect
                  value={isActiveFilter}
                  onChange={(e) => setIsActiveFilter(e.target.value)}
                >
                  <option value="all">All Teams</option>
                  <option value="true">✅ Active Only</option>
                  <option value="false">❌ Disabled Only</option>
                  <option value="forfeit">🏳️ Forfeit</option>
                </FilterSelect>
                <FilterSelect
                  value={unassignedDivisionFilter ? 'true' : 'all'}
                  onChange={(e) => setUnassignedDivisionFilter(e.target.value === 'true')}
                >
                  <option value="all">All Divisions</option>
                  <option value="true">⚠️ Unassigned Division Only</option>
                </FilterSelect>
              </SearchContainer>
            </TeamsHeader>
            
            {teamsLoading ? (
              <LoadingContainer>Loading teams...</LoadingContainer>
            ) : teamsError ? (
              <ErrorContainer>Error loading teams: {teamsError.message}</ErrorContainer>
            ) : (
              <>
                <style>{`
                  @media (max-width: 1280px) {
                    .hide-on-small {
                      display: none !important;
                    }
                  }
                  @media (max-width: 1440px) {
                    .hide-on-medium {
                      display: none !important;
                    }
                  }
                `}</style>
                {console.log('🔍 Rendering teams - teamsData:', teamsData)}
                {console.log('🔍 Teams array:', teamsData?.data?.teams)}
                {console.log('🔍 Teams count:', teamsData?.data?.teams?.length)}
              <TeamsTableWrapper>
              <TeamsTable>
                <TableHeader>
                  <tr>
                    <TableHeaderCell style={{ width: '50px' }}>#</TableHeaderCell>
                    <TableHeaderCell>Team</TableHeaderCell>
                    <TableHeaderCell>Canton</TableHeaderCell>
                    <TableHeaderCell>Reg. Canton</TableHeaderCell>
                    <TableHeaderCell>Skill Level</TableHeaderCell>
                    <TableHeaderCell>Division</TableHeaderCell>
                    <TableHeaderCell>Home club</TableHeaderCell>
                    <TableHeaderCell>Instagram</TableHeaderCell>
                    <TableHeaderCell>Phases</TableHeaderCell>
                    <TableHeaderCell>Members</TableHeaderCell>
                    <TableHeaderCell>Source</TableHeaderCell>
                    <TableHeaderCell>Reg. Type</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Joined</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </tr>
                </TableHeader>
                <TableBody>
                  {teamsData?.data?.teams?.length > 0 ? (
                    teamsData.data.teams.map((team, index) => {
                      const globalIndex = (currentPage - 1) * teamsPerPage + index + 1;
                      const status = adminTeamStatusDisplay(team);
                      return (
                    <TableRow key={team.id}>
                      <TableCell style={{ fontWeight: '600', color: '#6b7280' }}>
                        {globalIndex}
                      </TableCell>
                      <TableCell>
                        <TeamName 
                          style={{ cursor: 'pointer', color: '#043663' }}
                          onClick={() => handleViewTeamDetails(team)}
                          title="Click to view team details"
                        >
                          {team.team_name}
                        </TeamName>
                        <TeamDetails>ID: {team.id.substring(0, 8)}...</TeamDetails>
                      </TableCell>
                      <TableCell>{team.canton}</TableCell>
                      <TableCell className="hide-on-small">
                        <span style={{ 
                          color: team.registration_canton === team.canton ? '#059669' : '#6b7280',
                          fontSize: '0.875rem',
                          fontWeight: team.registration_canton === team.canton ? '500' : '400'
                        }}>
                          {team.registration_canton || 'N/A'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <SkillLevel>{team.team_skill_level}</SkillLevel>
                      </TableCell>
                      <TableCell>
                        {team.division_name ? (
                          <DivisionBadge>{team.division_name}</DivisionBadge>
                        ) : (
                          <span style={{ color: '#d52b1e', fontStyle: 'italic' }}>Unassigned</span>
                        )}
                      </TableCell>
                      <TableCell className="hide-on-small" style={{ maxWidth: '140px', fontSize: '0.8rem' }}>
                        {team.home_clubs || (
                          <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not set</span>
                        )}
                      </TableCell>
                      <TableCell className="hide-on-small" style={{ maxWidth: '120px', fontSize: '0.8rem' }}>
                        {team.member_instagrams || (
                          <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</span>
                        )}
                      </TableCell>
                      <TableCell className="hide-on-small">
                        {team.registered_seasons && Array.isArray(team.registered_seasons) && team.registered_seasons.length > 0 ? (
                          <SeasonBadgesList 
                            seasons={team.registered_seasons} 
                            selectedSeason={selectedSeason}
                            teamId={team.id}
                          />
                        ) : (
                          <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.875rem' }}>No phases</span>
                        )}
                      </TableCell>
                      <TableCell>{team.member_count}</TableCell>
                      <TableCell className="hide-on-small">
                        <SourceBadge $source={team.registration_source}>
                          {team.registration_source === 'meta' ? '📱 Meta' : 
                           team.registration_source === 'google' ? '🔍 Google' :
                           team.registration_source === 'direct' ? '🔗 Direct' :
                           team.registration_source === 'referral' ? '👥 Referral' :
                           team.registration_source === 'email' ? '📧 Email' :
                           team.registration_source === 'organic' ? '🌱 Organic' :
                           '❓ Unknown'}
                        </SourceBadge>
                        {team.utm_campaign && (
                          <TeamDetails style={{ fontSize: '0.7rem', marginTop: '0.25rem' }}>
                            {team.utm_campaign}
                          </TeamDetails>
                        )}
                      </TableCell>
                      <TableCell className="hide-on-medium">
                        {selectedSeason && team.registration_type ? (
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            display: 'inline-block',
                            backgroundColor: 
                              team.registration_type === 'reactivated' ? '#fef3c7' :
                              team.registration_type === 'auto-registered' ? '#dbeafe' :
                              team.registration_type === 'mislinked' ? '#fee2e2' :
                              team.registration_type === 'original' ? '#dcfce7' : '#f3f4f6',
                            color:
                              team.registration_type === 'reactivated' ? '#92400e' :
                              team.registration_type === 'auto-registered' ? '#1e40af' :
                              team.registration_type === 'mislinked' ? '#b91c1c' :
                              team.registration_type === 'original' ? '#166534' : '#6b7280',
                            border: 
                              team.registration_type === 'reactivated' ? '1px solid #f59e0b' :
                              team.registration_type === 'auto-registered' ? '1px solid #3b82f6' :
                              team.registration_type === 'mislinked' ? '1px solid #ef4444' :
                              team.registration_type === 'original' ? '1px solid #16a34a' : 'none'
                          }}>
                            {team.registration_type === 'reactivated' ? '🔄 Reactivated' :
                             team.registration_type === 'auto-registered' ? '⬇️ From Previous' :
                             team.registration_type === 'mislinked' ? '⚠️ Mislinked' :
                             team.registration_type === 'original' ? '⭐ Original' : '❓ Unknown'}
                          </span>
                        ) : (
                          <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.875rem' }}>N/A</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span style={{
                          color: status.color,
                          fontWeight: 'bold',
                          fontSize: '0.875rem'
                        }}>
                          {status.label}
                          {team.season_is_active !== undefined && selectedSeason && (
                            <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.5rem' }}>
                              (Phase)
                            </span>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="hide-on-small">
                        {new Date(team.phase_registered_at || team.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <ActionButton 
                          className="primary" 
                          onClick={() => handleViewTeamDetails(team)}
                          style={{ fontSize: '0.75rem', padding: '0.4rem 0.6rem', marginRight: '0.25rem', marginBottom: '0.25rem' }}
                        >
                          View
                        </ActionButton>
                        <ActionButton
                          onClick={() => handleEditTeam(team)}
                          style={{ fontSize: '0.75rem', padding: '0.4rem 0.6rem', marginRight: '0.25rem', marginBottom: '0.25rem' }}
                          title={
                            selectedSeason
                              ? 'Edit team name (global), canton, and division for this phase'
                              : 'Edit global team name only — select a phase to change canton or division'
                          }
                        >
                          Edit
                        </ActionButton>
                        {/* Only show disable/reactivate button when a specific season is selected */}
                        {selectedSeason && (
                          <ActionButton 
                            className={(team.season_is_active !== undefined ? team.season_is_active : team.is_active) ? "danger" : "success"} 
                            onClick={() => handleToggleTeamStatus(team)}
                            style={{ fontSize: '0.75rem', padding: '0.4rem 0.6rem', marginBottom: '0.25rem' }}
                          >
                            {(team.season_is_active !== undefined ? team.season_is_active : team.is_active) ? "Disable" : "Reactivate"}
                          </ActionButton>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                        No teams found. {teamsData ? 'Try adjusting your search filters.' : 'Loading teams...'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </TeamsTable>
              </TeamsTableWrapper>
              
              {/* Pagination Controls */}
              {teamsData?.data?.pagination && teamsData.data.pagination.pages > 1 && (
                <PaginationContainer>
                  <PaginationInfo>
                    Showing {((currentPage - 1) * teamsPerPage) + 1} to {Math.min(currentPage * teamsPerPage, teamsData.data.pagination.total)} of {teamsData.data.pagination.total} teams
                  </PaginationInfo>
                  <PaginationControls>
                    <PageButton 
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      ← Previous
                    </PageButton>
                    <PageNumbers>
                      {[...Array(teamsData.data.pagination.pages)].map((_, idx) => {
                        const pageNum = idx + 1;
                        // Show first page, last page, current page, and pages around current
                        const showPage = 
                          pageNum === 1 || 
                          pageNum === teamsData.data.pagination.pages ||
                          (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);
                        
                        const showEllipsis = 
                          (pageNum === currentPage - 2 && currentPage > 3) ||
                          (pageNum === currentPage + 2 && currentPage < teamsData.data.pagination.pages - 2);
                        
                        if (showEllipsis) {
                          return <span key={pageNum} style={{ padding: '0.5rem' }}>...</span>;
                        }
                        
                        if (!showPage) return null;
                        
                        return (
                          <PageButton
                            key={pageNum}
                            $active={currentPage === pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </PageButton>
                        );
                      })}
                    </PageNumbers>
                    <PageButton 
                      onClick={() => setCurrentPage(prev => Math.min(teamsData.data.pagination.pages, prev + 1))}
                      disabled={currentPage === teamsData.data.pagination.pages}
                    >
                      Next →
                    </PageButton>
                  </PaginationControls>
                </PaginationContainer>
              )}
              </>
            )}
          </TeamsContainer>
          
          {/* Season Selection Modal for Enabling Disabled Teams */}
          {showSeasonSelectionModal && teamToEnable && (
            <ModalOverlay onClick={handleCancelSeasonSelection}>
              <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                  <ModalTitle>{isDisablingTeam ? 'Disable Team in Phase' : 'Activate Team in Phase'}</ModalTitle>
                  <CloseButton onClick={handleCancelSeasonSelection}>×</CloseButton>
                </ModalHeader>
                <ModalBody>
                  <TeamInfoSection>
                    <h3 style={{ marginBottom: '1rem', color: '#1f2937' }}>Team Information</h3>
                    <TeamInfoGrid>
                      <InfoItem>
                        <div className="label">Team Name</div>
                        <div className="value">{teamToEnable.team_name}</div>
                      </InfoItem>
                      <InfoItem>
                        <div className="label">Canton</div>
                        <div className="value">{teamToEnable.canton || 'N/A'}</div>
                      </InfoItem>
                    </TeamInfoGrid>
                  </TeamInfoSection>

                  <div style={{ marginTop: '2rem' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '0.75rem', 
                      fontWeight: '600', 
                      fontSize: '0.875rem', 
                      color: '#374151' 
                    }}>
                      Select Phase to {isDisablingTeam ? 'Disable' : 'Activate'} Team In:
                    </label>
                    <select
                      value={selectedSeasonForEnable}
                      onChange={(e) => setSelectedSeasonForEnable(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">-- Select a phase --</option>
                      {seasonsData?.data?.map((season) => {
                        const now = new Date();
                        const startDate = new Date(season.start_date);
                        const endDate = new Date(season.end_date);
                        const regStart = new Date(season.registration_start);
                        const regEnd = new Date(season.registration_end);
                        
                        let statusLabel = '';
                        if (season.status === 'completed') statusLabel = ' (Completed)';
                        else if (now >= startDate && now <= endDate) statusLabel = ' (Active)';
                        else if (now >= regStart && now <= regEnd) statusLabel = ' (Registration Open)';
                        else statusLabel = ' (Upcoming)';
                        
                        return (
                          <option key={season.id} value={season.id}>
                            {season.name}{statusLabel}
                          </option>
                        );
                      })}
                    </select>
                    <p style={{ 
                      marginTop: '0.75rem', 
                      fontSize: '0.875rem', 
                      color: '#6b7280',
                      lineHeight: '1.5'
                    }}>
                      {isDisablingTeam ? (
                        <>
                          <strong>Note:</strong> Disabling this team for the selected phase will hide them from that phase's views, but their data and rankings from previous phases will remain intact and visible.
                        </>
                      ) : (
                        <>
                          <strong>Note:</strong> This team will be registered for the selected phase as a new team. 
                          Any stats or records from previous phases where they were disabled will not be carried over.
                        </>
                      )}
                    </p>
                  </div>

                  <div style={{ 
                    marginTop: '2rem', 
                    display: 'flex', 
                    gap: '1rem', 
                    justifyContent: 'flex-end' 
                  }}>
                    <button
                      onClick={handleCancelSeasonSelection}
                      style={{
                        padding: '0.75rem 1.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        color: '#374151',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleEnableTeamWithSeason}
                      disabled={!selectedSeasonForEnable}
                      style={{
                        padding: '0.75rem 1.5rem',
                        border: 'none',
                        borderRadius: '8px',
                        backgroundColor: selectedSeasonForEnable ? '#043663' : '#9ca3af',
                        color: 'white',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: selectedSeasonForEnable ? 'pointer' : 'not-allowed'
                      }}
                    >
                      {isDisablingTeam ? 'Disable Team' : 'Activate Team'}
                    </button>
                  </div>
                </ModalBody>
              </ModalContent>
            </ModalOverlay>
          )}

          {/* Edit Team Modal */}
          {editingTeam && (() => {
            const editTeamHasEdits = !selectedSeason
              ? editFormData.team_name !== editingTeam.team_name
              : isDivisionLocked
                ? editFormData.team_name !== editingTeam.team_name
                : (
                    editFormData.team_name !== editingTeam.team_name ||
                    editFormData.canton !== editingTeam.canton ||
                    normAdminDivisionId(editFormData.division_id) !== normAdminDivisionId(editingTeam.division_id)
                  );
            return (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '8px',
                width: '400px',
                maxWidth: '90vw'
              }}>
                <h3 style={{ marginBottom: '1rem', color: '#1f2937' }}>
                  {selectedSeason ? 'Edit team' : 'Edit team name (global)'}
                </h3>
                {!selectedSeason && (
                  <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1rem', lineHeight: 1.5 }}>
                    The name is stored on the team record and used for <strong>login</strong>, the team dashboard, and all seasons.
                    Select a season in the filter above to change canton or division for that season only.
                  </p>
                )}

                {/* Registration Canton Info (Read-only) */}
                {editingTeam.registration_canton && (
                  <div style={{ 
                    marginBottom: '1rem', 
                    padding: '0.75rem', 
                    background: '#f8fafc', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '6px' 
                  }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                      📍 Registration Canton
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {editingTeam.registration_canton} (Original registration - preserved for reference)
                    </div>
                  </div>
                )}
                
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Team Name
                  </label>
                  <input
                    type="text"
                    value={editFormData.team_name}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, team_name: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
                {selectedSeason && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Canton
                  </label>
                  <select
                    value={editFormData.canton}
                    onChange={(e) => {
                      if (isDivisionLocked) return;
                      const newCanton = e.target.value;
                      setEditFormData(prev => ({ ...prev, canton: newCanton, division_id: '' }));
                      
                      // Filter divisions for the new canton
                      if (newCanton) {
                        setAvailableDivisions(divisionsForEditCanton(allDivisions, newCanton));
                      } else {
                        setAvailableDivisions([]);
                      }
                    }}
                    disabled={isDivisionLocked}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      ...(isDivisionLocked ? { background: '#f3f4f6', cursor: 'not-allowed' } : {})
                    }}
                  >
                    <option value="">Select Canton</option>
                    <option value="AG">AG - Aargau</option>
                    <option value="Appenzell">Appenzell</option>
                    <option value="Basel">Basel</option>
                    <option value="BE">BE - Bern</option>
                    <option value="FR">FR - Fribourg</option>
                    <option value="GE">GE - Geneva</option>
                    <option value="GL">GL - Glarus</option>
                    <option value="Grisons">Grisons</option>
                    <option value="JU">JU - Jura</option>
                    <option value="LU">LU - Lucerne</option>
                    <option value="NE">NE - Neuchâtel</option>
                    <option value="Nidwalden">Nidwalden</option>
                    <option value="SH">SH - Schaffhausen</option>
                    <option value="SZ">SZ - Schwyz</option>
                    <option value="SO">SO - Solothurn</option>
                    <option value="SG">SG - St. Gallen</option>
                    <option value="TG">TG - Thurgau</option>
                    <option value="TI">TI - Ticino</option>
                    <option value="VS">VS - Valais</option>
                    <option value="VD">VD - Vaud</option>
                    <option value="ZG">ZG - Zug</option>
                    <option value="ZH">ZH - Zurich</option>
                  </select>
                </div>
                )}

                {/* Division Selection — only when a season is selected (season-specific assignment) */}
                {selectedSeason && (
                <DivisionLockWarning>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Division
                    </label>
                    <div style={{ fontSize: '0.75rem', color: '#059669', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Current Team: {editingTeam?.canton} - {editingTeam?.division_name || 'No Division Assigned'}
                    </div>
                    <select
                      value={editFormData.division_id}
                      onChange={(e) => {
                        if (isDivisionLocked) return;
                        console.log('🔍 Division changed to:', e.target.value);
                        setEditFormData(prev => ({ ...prev, division_id: e.target.value }));
                      }}
                      disabled={isDivisionLocked}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        ...(isDivisionLocked ? { background: '#f3f4f6', cursor: 'not-allowed' } : {})
                      }}
                    >
                      <option value="">No Division Assigned</option>
                      {availableDivisions.map(division => (
                          <option key={division.id} value={division.id}>
                            {formatTeamEditDivisionLabel(division)}
                          </option>
                      ))}
                    </select>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                      ℹ️ You can change the canton, division, or both. After making changes, use the Match Generation tab to regenerate matches when ready.
                    </div>
                  </div>
                </DivisionLockWarning>
                )}

                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleCancelEdit}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      background: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveTeamEdit}
                    disabled={!editTeamHasEdits || savingTeamEdit}
                    style={{
                      padding: '0.5rem 1rem',
                      border: 'none',
                      background: editTeamHasEdits && !savingTeamEdit ? '#043663' : '#9ca3af',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: editTeamHasEdits && !savingTeamEdit ? 'pointer' : 'not-allowed',
                      opacity: editTeamHasEdits && !savingTeamEdit ? 1 : 0.6
                    }}
                    title={
                      !selectedSeason
                        ? 'Saves the global team name only'
                        : isDivisionLocked
                          ? 'Only team name can be saved while divisions are locked'
                          : 'Save team changes'
                    }
                  >
                    {savingTeamEdit ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
            );
          })()}
          </>
        );

      case 'team-provisioning':
        return <AdminTeamProvisioningPanel />;
      
      case 'divisions':
      case 'division-lock':
        return (
          <AdminDivisionsSection
            adminUser={adminUser?.name || 'admin'}
            canReadDivisionLock={canReadSection('division_lock')}
            initialTab={activeSection === 'division-lock' ? 'lock' : 'overview'}
          />
        );
      
      case 'league-management':
        return (
          <AdminLeagueManagementV2
            onGoToTeams={() => setActiveSection('teams')}
            onGoToPlayers={isCountryTenant ? () => setActiveSection('players') : undefined}
          />
        );
      
      case 'matches':
        return <AdminMatchesNew />;

      case 'matches-by-division':
        return <AdminMatchesByDivision />;

      case 'scheduled-matches':
        return <AdminScheduledMatches />;
      
      case 'promo-codes':
      case 'partner-communications':
      case 'attention':
      case 'league-statistics':
      case 'analytics':
      case 'registration-analytics':
      case 'settings-analytics':
        return <AdminAnalyticsHub onNavigate={navigateAdmin} canReadSection={canReadSection} />;

      case 'promotion-system':
      case 'promotion-relegation':
      case 'campaign-carry-promotion':
        return <AdminPromotionSystemHub />;
      
      case 'teammate-finder':
        return <AdminTeammateFinderSection />;
      
      case 'settings-general':
        return <AdminSettingsGeneral />;
      case 'settings-clubs':
        return <AdminSettingsClubs />;
      case 'organised-events':
        return <AdminOrganisedEvents />;
      case 'settings-promo-codes':
        return <AdminSettingsPromoCreate />;
      case 'settings-registration-products':
        return <AdminSettingsRegistrationProducts />;
      case 'registration-orders':
        return <AdminRegistrationOrders />;
      case 'shop':
        return <AdminShop />;
      case 'settings-tournaments':
        return <AdminSettingsTournaments />;
      case 'settings-email':
        return <AdminSettingsEmail canSend={canEditSection('settings_general')} />;

      default:
        return <div><h3>Select a menu item</h3></div>;
    }
  };


  if (!adminUser) {
    return <div>Loading...</div>;
  }

  // When URL has a tenant slug, wait until we've set X-Tenant-ID so all API calls use the correct tenant.
  if (tenant && !tenantContextReady) {
    console.log('[Tenant] Dashboard: rendering Loading tenant… (tenantContextReady=false)', { tenant });
    return (
      <DashboardContainer>
        <MainContent style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '1rem' }}>Loading tenant…</div>
        </MainContent>
      </DashboardContainer>
    );
  }

  const currentTenantForRequests = getAdminTenantInfo();
  console.log('[Tenant] Dashboard: rendering MAIN content', { tenantFromUrl: tenant, tenantContextReady, localStorageTenant: currentTenantForRequests });
  return (
    <DashboardContainer>
      <HamburgerButton
        aria-label="Toggle menu"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? '✕' : '☰'}
      </HamburgerButton>
      
      <SidebarOverlay $isOpen={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      
      <Sidebar $isOpen={sidebarOpen}>
        <SidebarHeader>
          <div className="logo-section">
            <img 
              src="/images/logo/logo.png" 
              alt="Helvetic Padel Series Logo" 
              onClick={handleLogoClick}
              style={{ cursor: 'pointer', transition: 'transform 0.2s ease' }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            />
            <h1>Admin Portal</h1>
            {tenant && (
              <p style={{ 
                fontSize: '0.75rem', 
                opacity: 0.8, 
                marginTop: '0.25rem',
                textTransform: 'capitalize'
              }}>
                {tenant.replace('-', ' ')}
              </p>
            )}
          </div>
          <div className="admin-info">
            <p>Welcome, {adminUser.name}</p>
            <p>{adminUser.role}</p>
          </div>
          {adminUser.isSuperAdmin && (
            <NavItem
              onClick={() => {
                clearAdminTenantInfo();
                navigate('/admin/tenants');
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
              style={{ marginTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '0.5rem' }}
            >
              <span className="nav-icon">←</span>
              <span className="nav-text">Back to tenant list</span>
            </NavItem>
          )}
        </SidebarHeader>
        
        <Navigation>
          {filteredMenuItems.map((item) => {
            // Insert "Upcoming Matches" accordion after "League Management"
            if (item.id === 'league-management') {
              return (
                <React.Fragment key={item.id}>
                  <NavItem
                    className={activeSection === item.id ? 'active' : ''}
                    onClick={() => {
                      setActiveSection(item.id);
                      if (window.innerWidth <= 768) {
                        setSidebarOpen(false);
                      }
                    }}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    <span className="nav-text">{item.label}</span>
                  </NavItem>
                  
                  {/* Upcoming Matches Accordion */}
                  {filteredUpcomingMatchesItems.length > 0 && (
                  <>
                  <NavItem
                    $isOpen={upcomingMatchesOpen}
                    className={filteredUpcomingMatchesItems.some(subItem => activeSection === subItem.id) ? 'active' : ''}
                    onClick={() => {
                      setUpcomingMatchesOpen(!upcomingMatchesOpen);
                    }}
                  >
                    <span className="nav-icon">⚽</span>
                    <span className="nav-text">Upcoming Matches</span>
                    <span className="nav-arrow">▶</span>
                  </NavItem>
                  
                  <AccordionSubmenu $isOpen={upcomingMatchesOpen}>
                    {filteredUpcomingMatchesItems.map((subItem) => (
                      <AccordionSubItem
                        key={subItem.id}
                        className={activeSection === subItem.id ? 'active' : ''}
                        onClick={() => {
                          setActiveSection(subItem.id);
                          if (window.innerWidth <= 768) {
                            setSidebarOpen(false);
                          }
                        }}
                      >
                        <span className="nav-icon">{subItem.icon}</span>
                        <span>{subItem.label}</span>
                      </AccordionSubItem>
                    ))}
                  </AccordionSubmenu>
                  </>
                  )}
                </React.Fragment>
              );
            }

            if (item.id === 'teammate-finder') {
              return (
                <NavItem
                  key={item.id}
                  className={activeSection === item.id ? 'active' : ''}
                  onClick={() => {
                    setActiveSection(item.id);
                    if (window.innerWidth <= 768) {
                      setSidebarOpen(false);
                    }
                  }}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-text">{item.label}</span>
                </NavItem>
              );
            }
            
            return (
              <NavItem
                key={item.id}
                className={activeSection === item.id ? 'active' : ''}
                onClick={() => {
                  setActiveSection(item.id);
                  if (window.innerWidth <= 768) {
                    setSidebarOpen(false);
                  }
                }}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-text">{item.label}</span>
              </NavItem>
            );
          })}
          {/* Settings accordion: show whenever user has any Settings permission (not tied to Teammate finder) */}
          {filteredSettingsItems.length > 0 && (
            <>
              <NavItem
                $isOpen={settingsOpen}
                className={filteredSettingsItems.some(subItem => activeSection === subItem.id) ? 'active' : ''}
                onClick={() => {
                  setSettingsOpen(!settingsOpen);
                }}
              >
                <span className="nav-icon">⚙️</span>
                <span className="nav-text">Settings</span>
                <span className="nav-arrow">▶</span>
              </NavItem>
              <AccordionSubmenu $isOpen={settingsOpen}>
                {filteredSettingsItems.map((subItem) => (
                  <AccordionSubItem
                    key={subItem.id}
                    className={activeSection === subItem.id ? 'active' : ''}
                    onClick={() => {
                      setActiveSection(subItem.id);
                      if (window.innerWidth <= 768) {
                        setSidebarOpen(false);
                      }
                    }}
                  >
                    <span className="nav-icon">{subItem.icon}</span>
                    <span>{subItem.label}</span>
                  </AccordionSubItem>
                ))}
              </AccordionSubmenu>
            </>
          )}
        </Navigation>
      </Sidebar>

      <MainContent>
        <TopBar>
          <PageTitle>
            {filteredSettingsItems.find(item => item.id === activeSection)?.label
              ? `Settings – ${filteredSettingsItems.find(item => item.id === activeSection).label}`
              : filteredMenuItems.find(item => item.id === activeSection)?.label || 'Dashboard'}
          </PageTitle>
          <LogoutButton onClick={handleLogout}>
            Logout
          </LogoutButton>
        </TopBar>

        <ContentArea>
          {renderContent()}
        </ContentArea>
      </MainContent>

      {/* Team Details Modal */}
      {showTeamDetails && selectedTeam && (
        <ModalOverlay onClick={handleCloseTeamDetails}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Team Details - {selectedTeam.team_name}</ModalTitle>
              <CloseButton onClick={handleCloseTeamDetails}>×</CloseButton>
            </ModalHeader>
            
            <ModalBody>
              {/* Team Logo */}
              <TeamLogoSection>
                {selectedTeam.team_logo_url ? (
                  <TeamLogoImg src={selectedTeam.team_logo_url} alt={`${selectedTeam.team_name} logo`} />
                ) : (
                  <TeamLogoPlaceholder>
                    {getTeamInitials(selectedTeam.team_name)}
                  </TeamLogoPlaceholder>
                )}
                <TeamLogoActions>
                  <input
                    ref={teamLogoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,image/tiff"
                    style={{ display: 'none' }}
                    onChange={handleTeamLogoUpload}
                  />
                  <ActionButton
                    type="button"
                    className="primary"
                    disabled={logoActionInProgress}
                    onClick={() => teamLogoInputRef.current?.click()}
                  >
                    {logoActionInProgress ? 'Updating…' : 'Change logo'}
                  </ActionButton>
                  {selectedTeam.team_logo_url && (
                    <ActionButton
                      type="button"
                      className="danger"
                      disabled={logoActionInProgress}
                      onClick={handleRemoveTeamLogo}
                    >
                      Remove logo
                    </ActionButton>
                  )}
                </TeamLogoActions>
              </TeamLogoSection>

              {/* Team Information */}
              <TeamInfoSection>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>
                  Team Information
                </h3>
                <TeamInfoGrid>
                  <InfoItem>
                    <div className="label">Team Name</div>
                    <div className="value">{selectedTeam.team_name}</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Canton</div>
                    <div className="value">{selectedTeam.canton}</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Skill Level</div>
                    <div className="value">
                      <SkillLevelBadge $level={selectedTeam.team_skill_level}>
                        {selectedTeam.team_skill_level}
                      </SkillLevelBadge>
                    </div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Division</div>
                    <div className="value">
                      {selectedTeam.division_name ? (
                        <span style={{ 
                          padding: '0.25rem 0.5rem', 
                          background: '#dbeafe', 
                          color: '#043663', 
                          borderRadius: '4px', 
                          fontSize: '0.75rem', 
                          fontWeight: '500' 
                        }}>
                          {selectedTeam.division_name}
                        </span>
                      ) : (
                        <span style={{ color: '#d52b1e', fontStyle: 'italic' }}>Unassigned</span>
                      )}
                    </div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Member Count</div>
                    <div className="value">{selectedTeam.member_count}/2</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Team Phone Number</div>
                    <div className="value">{selectedTeam.phone_number || 'Not provided'}</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Joined</div>
                    <div className="value">
                      {new Date(selectedTeam.phase_registered_at || selectedTeam.created_at).toLocaleDateString()}
                    </div>
                  </InfoItem>
                </TeamInfoGrid>
              </TeamInfoSection>

              {/* Team Members */}
              <MembersSection>
                <MembersTitle>Team Members</MembersTitle>
                
                {loadingMembers ? (
                  <LoadingSpinner>Loading team members...</LoadingSpinner>
                ) : teamMembers.length > 0 ? (
                  <AdminTeamMembersGrid>
                    {teamMembers.map((member) => (
                      <AdminTeamMemberProfileCard
                        key={member.id}
                        member={member}
                        teamId={selectedTeam?.id}
                        seasonId={selectedSeason || undefined}
                        onPaymentUpdated={refreshTeamDetailsMembers}
                      />
                    ))}
                  </AdminTeamMembersGrid>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '2rem', 
                    color: '#6b7280',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    No team members found
                  </div>
                )}
              </MembersSection>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </DashboardContainer>
  );
};

export default AdminDashboardPage;
